import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import api, { API_BASE_URL } from '../utils/api';
import AttachmentPreview from './AttachmentPreview';
import { useChatContext } from '../context/ChatContext';
import { 
  CheckCircle, 
  Circle, 
  Clock, 
  User, 
  Calendar, 
  Flag, 
  MessageSquare, 
  Paperclip, 
  Plus, 
  Edit3, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  Target as TargetIcon,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Play,
  Pause,
  XCircle,
  LayoutGrid,
  List,
  Columns3,
  Settings,
  Upload,
  Download,
  MessageCircle,
  Send,
  X,
  CheckSquare,
  Tag,
  Link,
  FileText,
  ExternalLink,
  Eye,
  Trash,
  Archive,
  ArchiveRestore,
  RefreshCw,
  FolderInput
} from 'lucide-react';
import StageEditModal from './StageEditModal';
import TimeTracking from './TimeTracking';
import RecurringTaskModal from './RecurringTaskModal';
import InputWithVariablePicker from './InputWithVariablePicker';
import SubtaskCompletionModal from './SubtaskCompletionModal';
import UserProfileModal from './UserProfileModal';
import RichTextEditor from './RichTextEditor';
import DeliverableViewModal from './DeliverableViewModal';
import { validateTaskCompletion, isMovingToCompletion } from '../utils/taskUtils';
import { renderLinkifiedText } from '../utils/textUtils';

// Priority configuration
const PRIORITIES = {
  Low: { color: 'text-gray-600', bgColor: 'bg-gray-100', icon: Circle },
  Normal: { color: 'text-blue-600', bgColor: 'bg-blue-100', icon: Circle },
  High: { color: 'text-orange-600', bgColor: 'bg-orange-100', icon: AlertTriangle },
  Critical: { color: 'text-red-600', bgColor: 'bg-red-100', icon: AlertTriangle }
};

// Status configuration
const STATUSES = {
  'To Do': { color: 'text-gray-600', bgColor: 'bg-gray-100', icon: Circle },
  'In Progress': { color: 'text-blue-600', bgColor: 'bg-blue-100', icon: Play },
  'Blocked': { color: 'text-red-600', bgColor: 'bg-red-100', icon: XCircle },
  'Done': { color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircle }
};

// Utility function to convert 24-hour time to 12-hour format
const formatTimeTo12Hour = (time24) => {
  if (!time24) return '';
  
  try {
    // Handle both HH:MM and HH:MM:SS formats
    const [hours, minutes] = time24.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) return time24;
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (e) {
    return time24; // Return original if parsing fails
  }
};

// Helper function to build recurring rule string from UI state
const buildRecurringRule = (frequency, days, monthDay, yearDate) => {
  switch (frequency) {
    case 'daily':
      return 'daily';
    case 'weekly':
      if (days && days.length > 0) {
        const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const selectedDays = days.map(d => dayNames[d - 1]).join(',');
        return `weekly:${selectedDays}`;
      }
      return 'weekly';
    case 'monthly':
      if (monthDay === 'last') {
        return 'monthly:last';
      }
      return `monthly:${monthDay}`;
    case 'yearly':
      return `yearly:${yearDate}`;
    default:
      return '';
  }
};

// Helper function to parse recurring rule string to UI state
const parseRecurringRule = (rule) => {
  if (!rule) return { frequency: 'daily', days: [], monthDay: '1', yearDate: '01-01', interval: 1 };
  
  // Try to parse as JSON first (new format from RecurringTaskModal)
  try {
    const parsed = JSON.parse(rule);
    return {
      frequency: parsed.frequency || 'daily',
      days: parsed.daysOfWeek || [],
      monthDay: String(parsed.monthlyDate || '1'),
      yearDate: '01-01',
      interval: parsed.interval || 1,
      monthlyType: parsed.monthlyType || 'date',
      monthlyWeek: parsed.monthlyWeek || 1,
      monthlyDay: parsed.monthlyDay || 1,
      startTime: parsed.startTime || '',
      endTime: parsed.endTime || '',
      endType: parsed.endType || 'never',
      endAfter: parsed.endAfter || 10,
      endBy: parsed.endBy || '',
      dueDateOffset: parsed.dueDateOffset || 0,
      dueDatePeriod: parsed.dueDatePeriod || 'days',
      isRecurringTemplate: parsed.isRecurringTemplate || false
    };
  } catch {
    // Fall back to legacy string format parsing
  }
  
  const ruleLower = rule.toLowerCase().trim();
  
  if (ruleLower === 'daily') {
    return { frequency: 'daily', days: [], monthDay: '1', yearDate: '01-01', interval: 1 };
  }
  
  if (ruleLower.startsWith('weekly')) {
    const parts = rule.split(':');
    if (parts.length > 1) {
      const dayNames = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7 };
      const days = parts[1].split(',').map(d => dayNames[d.trim().toLowerCase()] || 0).filter(d => d > 0);
      return { frequency: 'weekly', days, monthDay: '1', yearDate: '01-01', interval: 1 };
    }
    return { frequency: 'weekly', days: [], monthDay: '1', yearDate: '01-01', interval: 1 };
  }
  
  if (ruleLower.startsWith('monthly')) {
    const parts = rule.split(':');
    if (parts.length > 1) {
      const monthDay = parts[1].trim();
      return { frequency: 'monthly', days: [], monthDay, yearDate: '01-01', interval: 1 };
    }
    return { frequency: 'monthly', days: [], monthDay: '1', yearDate: '01-01', interval: 1 };
  }
  
  if (ruleLower === 'quarterly') {
    return { frequency: 'monthly', days: [], monthDay: '1', yearDate: '01-01', interval: 3 };
  }
  
  if (ruleLower.startsWith('yearly')) {
    const parts = rule.split(':');
    if (parts.length > 1) {
      return { frequency: 'yearly', days: [], monthDay: '1', yearDate: parts[1].trim(), interval: 1 };
    }
    return { frequency: 'yearly', days: [], monthDay: '1', yearDate: '01-01', interval: 1 };
  }
  
  return { frequency: 'daily', days: [], monthDay: '1', yearDate: '01-01', interval: 1 };
};

// Generate human-readable preview text from recurring rule
const generateRecurringPreview = (rule) => {
  if (!rule) return '';
  
  try {
    const config = typeof rule === 'string' ? JSON.parse(rule) : rule;
    const { frequency, interval, daysOfWeek, monthlyType, monthlyDate, monthlyWeek, monthlyDay, startTime, endTime } = config;
    
    const weekDays = [
      { value: 0, label: 'Sun' },
      { value: 1, label: 'Mon' },
      { value: 2, label: 'Tue' },
      { value: 3, label: 'Wed' },
      { value: 4, label: 'Thu' },
      { value: 5, label: 'Fri' },
      { value: 6, label: 'Sat' }
    ];
    
    let text = 'Repeats ';
    
    if (frequency === 'daily') {
      if (daysOfWeek && daysOfWeek.length > 0) {
        if (daysOfWeek.length === 5 && [1,2,3,4,5].every(d => daysOfWeek.includes(d))) {
          text += 'on weekdays';
        } else {
          const dayNames = daysOfWeek.sort().map(d => weekDays.find(day => day.value === d)?.label).join(', ');
          text += `on ${dayNames}`;
        }
      } else {
        text += interval === 1 ? 'daily' : `every ${interval} days`;
      }
    } else if (frequency === 'weekly') {
      if (daysOfWeek && daysOfWeek.length > 0) {
        const dayNames = daysOfWeek.sort().map(d => weekDays.find(day => day.value === d)?.label).join(', ');
        text += interval === 1 ? `weekly on ${dayNames}` : `every ${interval} weeks on ${dayNames}`;
      } else {
        text += interval === 1 ? 'weekly' : `every ${interval} weeks`;
      }
    } else if (frequency === 'monthly') {
      const monthText = interval === 3 ? 'quarterly' : (interval === 1 ? 'monthly' : `every ${interval} months`);
      if (monthlyType === 'date' && monthlyDate) {
        const suffix = ['th', 'st', 'nd', 'rd'][(monthlyDate % 100 - 20) % 10] || ['th', 'st', 'nd', 'rd'][monthlyDate % 100] || 'th';
        text += `${monthText} on the ${monthlyDate}${suffix}`;
      } else {
        text += monthText;
      }
    } else if (frequency === 'yearly') {
      text += interval === 1 ? 'yearly' : `every ${interval} years`;
    }
    
    if (startTime) {
      const [hours, minutes] = startTime.split(':');
      const hour12 = hours % 12 || 12;
      const ampm = hours < 12 ? 'AM' : 'PM';
      text += ` at ${hour12}:${minutes} ${ampm}`;
    }
    
    return text;
  } catch {
    return 'Custom recurring schedule';
  }
};

// Component to render individual comments with nested replies (moved outside to prevent re-renders)
const CommentItem = ({ comment, depth = 0, replyingTo, replyText, setReplyText, handleStartReply, handleCancelReply, handleAddComment, handleRetryComment, user, openAttachmentPreview, isCommentSending }) => (
  <div className={`${depth > 0 ? 'ml-8 mt-3' : ''}`}>
    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-medium overflow-hidden">
            {comment.user_avatar_url && comment.user_avatar_url.trim() !== '' && !comment.user_avatar_url.includes('example.com') ? (
              <img 
                src={comment.user_avatar_url} 
                alt={comment.user_name}
                className="w-6 h-6 object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = comment.user_name?.split(' ').map(n => n[0]).join('') || 'U';
                }}
              />
            ) : (
              comment.user_name?.split(' ').map(n => n[0]).join('') || 'U'
            )}
          </div>
          <div className="font-medium text-gray-900 text-sm">{comment.user_name}</div>
        </div>
        <div className="text-xs text-gray-500">
          {new Date(comment.created_at).toLocaleString()}
        </div>
      </div>
      <p className="text-gray-700 text-sm mb-2 whitespace-pre-wrap">{renderLinkifiedText(comment.content)}</p>

      {(comment.status === 'sending' || comment.status === 'failed') && (
        <div className="flex items-center space-x-2 text-xs mb-2">
          {comment.status === 'sending' ? (
            <span className="text-gray-500">Sending...</span>
          ) : (
            <>
              <span className="text-red-500">Failed to send</span>
              <button
                onClick={() => handleRetryComment && handleRetryComment(comment)}
                className="text-purple-600 hover:text-purple-700"
                disabled={isCommentSending}
              >
                Retry
              </button>
            </>
          )}
        </div>
      )}
      
      {/* Comment attachments */}
      {comment.attachments && comment.attachments.length > 0 && (
        <div className="mt-2 space-y-1">
          {comment.attachments.map((attachment, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openAttachmentPreview && openAttachmentPreview(attachment);
              }}
              className="flex items-center space-x-2 text-xs bg-gray-50 px-2 py-1 rounded hover:bg-purple-50 hover:border-purple-200 border border-transparent transition-colors w-full text-left"
            >
              <Paperclip className="w-3 h-3 text-gray-500" />
              <span className="flex-1 truncate text-gray-700">{attachment.name}</span>
              <Eye className="w-3 h-3 text-purple-500" />
            </button>
          ))}
        </div>
      )}
      
      {/* Reply button */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => handleStartReply(comment.id)}
          className="text-xs text-gray-500 hover:text-purple-600 transition-colors flex items-center space-x-1"
        >
          <MessageCircle className="w-3 h-3" />
          <span>Reply</span>
        </button>
        {comment.replies && comment.replies.length > 0 && (
          <span className="text-xs text-gray-400">
            {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
          </span>
        )}
      </div>

      {/* Reply input for this comment */}
      {replyingTo === comment.id && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
              {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
            </div>
            <div className="flex-1">
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                rows="2"
                placeholder={`Reply to ${comment.user_name}...`}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                autoFocus
              />
              <div className="flex items-center justify-end space-x-2 mt-2">
                <button 
                  onClick={handleCancelReply}
                  className="px-3 py-1 text-gray-600 text-sm hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleAddComment(comment.id)}
                  disabled={!replyText.trim() || isCommentSending}
                  className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isCommentSending ? 'Sending...' : 'Reply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Render nested replies */}
    {comment.replies && comment.replies.length > 0 && (
      <div className="mt-2">
        {comment.replies.map((reply) => (
          <CommentItem 
            key={reply.id} 
            comment={reply} 
            depth={depth + 1}
            replyingTo={replyingTo}
            replyText={replyText}
            setReplyText={setReplyText}
            handleStartReply={handleStartReply}
            handleCancelReply={handleCancelReply}
            handleAddComment={handleAddComment}
            handleRetryComment={handleRetryComment}
            user={user}
            openAttachmentPreview={openAttachmentPreview}
            isCommentSending={isCommentSending}
          />
        ))}
      </div>
    )}
  </div>
);

const TaskModal = ({ isOpen, onClose, task, onSave, onDelete, onArchive, project, teamMembers = [], stages = [], user, openAttachmentPreview, onEditSubtask }) => {
  const [activeSubTab, setActiveSubTab] = useState('subtasks');
  const [activityVisible, setActivityVisible] = useState(true);
  const [showTaskDetails, setShowTaskDetails] = useState(true);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringPreview, setRecurringPreview] = useState('');
  const [taskCategories, setTaskCategories] = useState([]);
  
  // Deliverable viewing state (internal to TaskModal for portability)
  const [viewingDeliverable, setViewingDeliverable] = useState(null);
  
  // Template selection states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  
  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        // Don't close if a nested modal is open
        if (showTemplateModal || showRecurringModal) return;
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onClose, showTemplateModal, showRecurringModal]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    workspace_content: '',
    workspace_updated_at: null,
    workspace_updated_by: null,
    priority: 'Normal',
    status: 'To Do',
    due_date: '',
    due_time: '',
    start_date: '',
    estimated_effort_value: '',
    estimated_effort_unit: 'hours',
    category: '',
    assignees: [],
    tags: [],
    is_milestone: false,
    recurring_rule: '',
    is_recurring_template: false,
    recurring_due_time: '',
    recurring_start_time: '',
    review_notes: '',
    deliverable_links: [],
    deliverable_files: [],
    checklist_items: [],
    template_id: null  // Track which template was used
  });
  
  // Recurring rule builder state
  const [recurringFrequency, setRecurringFrequency] = useState('daily');
  const [recurringDays, setRecurringDays] = useState([]);
  const [recurringMonthDay, setRecurringMonthDay] = useState('1');
  const [recurringYearDate, setRecurringYearDate] = useState('01-01');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  // Comment mentions and attachments
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionSearchTerm, setMentionSearchTerm] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [uploadingCommentFile, setUploadingCommentFile] = useState(false);
  const [isCommentSending, setIsCommentSending] = useState(false);
  const commentSendLockRef = useRef(false);
  const lastCommentSendAtRef = useRef(0);
  
  // Ref for comments container to enable auto-scroll
  const commentsContainerRef = useRef(null);
  
  const [subtasks, setSubtasks] = useState([]);
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [newSubtaskData, setNewSubtaskData] = useState({
    name: '',
    description: '',
    priority: 'Normal',
    status: 'To Do',
    assignees: [],
    due_date: '',
    estimated_effort_value: '',
    estimated_effort_unit: 'hours'
  });
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [pendingSubtasks, setPendingSubtasks] = useState([]); // For new tasks before ID exists
  const [pendingAttachments, setPendingAttachments] = useState([]); // For new tasks before ID exists
  const [quickEditSubtaskId, setQuickEditSubtaskId] = useState(null); // For quick edit dropdown
  const [quickEditField, setQuickEditField] = useState(null); // Which field is being edited
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Auto-save states
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  
  // Deliverables states
  const [deliverables, setDeliverables] = useState([]);
  const [newDeliverableLink, setNewDeliverableLink] = useState('');
  const [newDeliverableTitle, setNewDeliverableTitle] = useState('');
  const [showDeliverableForm, setShowDeliverableForm] = useState(false);
  const [uploadingDeliverable, setUploadingDeliverable] = useState(false);
  const [editingDeliverable, setEditingDeliverable] = useState(null);
  const [editDeliverableData, setEditDeliverableData] = useState({
    title: '',
    description: '',
    review_notes: '',
    status: 'pending'
  });

  // Dependencies states
  const [dependencies, setDependencies] = useState({ blocking: [], blocked_by: [], is_blocked: false });
  const [showAddDependencyDropdown, setShowAddDependencyDropdown] = useState(false);
  const [dependencySearchTerm, setDependencySearchTerm] = useState('');
  const [dependencySearchResults, setDependencySearchResults] = useState([]);
  const [searchingDependencies, setSearchingDependencies] = useState(false);
  const [addingDependency, setAddingDependency] = useState(false);
  const [dependencyType, setDependencyType] = useState('blocked_by'); // 'blocked_by' or 'blocks'

  // Structured estimated effort state
  const [effortValue, setEffortValue] = useState('');
  const [effortUnit, setEffortUnit] = useState('Minutes');

  // Track last loaded task ID to prevent redundant API calls
  const lastLoadedTaskIdRef = useRef(null);
  
  // Track if categories have been loaded to prevent redundant API calls
  const categoriesLoadedRef = useRef(false);
  
  // Track last form data set to prevent redundant setFormData calls
  const lastFormDataTaskIdRef = useRef(null);
  
  // Track if a template was just applied (to prevent useEffect from resetting)
  const templateAppliedRef = useRef(false);
  
  // Prevent create-mode form resets while modal is open
  const createFormInitializedRef = useRef(false);

  // Helper: Convert structured format to minutes (for storage)
  const effortToMinutes = (value, unit) => {
    if (!value || isNaN(value)) {
      return null;
    }
    const numValue = parseFloat(value);
    
    const multipliers = {
      'minutes': 1,
      'hours': 60,
      'days': 480 // 8 work hours per day
    };
    
    return Math.round(numValue * (multipliers[unit?.toLowerCase()] || 1));
  };

  // Helper: Parse recurring rule to UI state
  const parseRecurringRuleToUI = (rule) => {
    const parsed = parseRecurringRule(rule);
    setRecurringFrequency(parsed.frequency);
    setRecurringDays(parsed.days);
    setRecurringMonthDay(parsed.monthDay);
    setRecurringYearDate(parsed.yearDate);
  };
  
  // Subtask completion validation states
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [pendingTaskSave, setPendingTaskSave] = useState(null);
  
  // User profile modal states
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Move to project states
  const [showMoveProjectModal, setShowMoveProjectModal] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [movingTask, setMovingTask] = useState(false);

  // Fetch task categories from settings
  const fetchTaskCategories = async () => {
    try {
      const response = await api.get('/task-categories');
      setTaskCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch task categories:', error);
      // Fallback to empty array - user hasn't set up categories yet
      setTaskCategories([]);
    }
  };

  // Fetch task templates
  const fetchTaskTemplates = async () => {
    try {
      const response = await api.get('/templates/tasks');
      setTaskTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch task templates:', error);
      console.error('Error details:', error.response?.data);
      setTaskTemplates([]);
    }
  };

  // Apply template to form (only fill empty fields)
  const applyTemplate = (template) => {
    // Calculate due date from offset if provided
    let dueDate = '';
    if (template.due_date_offset !== null && template.due_date_offset !== undefined && template.due_date_offset !== '') {
      try {
        const offset = parseInt(template.due_date_offset, 10);
        if (!isNaN(offset)) {
          const today = new Date();
          today.setDate(today.getDate() + offset);
          if (!isNaN(today.getTime())) {
            dueDate = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          }
        }
      } catch (e) {
        console.warn('Failed to calculate due date from offset:', e);
      }
    }
    
    // Calculate start date from offset if provided
    let startDate = '';
    if (template.start_date_offset !== null && template.start_date_offset !== undefined && template.start_date_offset !== '') {
      try {
        const offset = parseInt(template.start_date_offset, 10);
        if (!isNaN(offset)) {
          const today = new Date();
          today.setDate(today.getDate() + offset);
          if (!isNaN(today.getTime())) {
            startDate = today.toISOString().split('T')[0];
          }
        }
      } catch (e) {
        console.warn('Failed to calculate start date from offset:', e);
      }
    }
    
    setFormData(prevData => ({
      ...prevData,
      name: template.name || prevData.name || '',
      description: template.description || prevData.description || '',
      priority: template.priority || prevData.priority || 'Normal',
      category: template.category || prevData.category || '',
      tags: template.tags?.length > 0 ? template.tags : prevData.tags,
      is_milestone: template.is_milestone || prevData.is_milestone,
      status: template.default_stage || prevData.status || 'To Do',
      due_date: dueDate || prevData.due_date || '',
      due_time: template.due_time || prevData.due_time || '',
      start_date: startDate || prevData.start_date || '',
      assignees: template.assignees?.length > 0 ? template.assignees : prevData.assignees,
      checklist_items: template.checklist_items?.length > 0 ? template.checklist_items : prevData.checklist_items,
      template_id: template.id
    }));
    
    // Apply subtasks from template (convert to pending subtasks format for new tasks)
    if (template.subtasks?.length > 0) {
      const templateSubtasks = template.subtasks.map((subtask, index) => ({
        id: `pending-${Date.now()}-${index}`,
        name: subtask.name || subtask,
        status: 'To Do',
        is_pending: true,
        priority: subtask.priority || 'Normal',
        estimated_effort_value: subtask.estimated_effort_value || null,
        estimated_effort_unit: subtask.estimated_effort_unit || 'hours',
        description: subtask.description || ''
      }));
      setPendingSubtasks(templateSubtasks);
      console.log('📋 Template subtasks applied:', templateSubtasks);
    }
    
    // Set effort value and unit from the template's new fields
    if (template.estimated_effort_value !== null && template.estimated_effort_value !== undefined) {
      const effortValue = template.estimated_effort_value;
      
      // Map the template's unit to the frontend format (capitalize first letter)
      let effortUnit = 'Hours'; // default
      const unit = (template.estimated_effort_unit || 'hours').toLowerCase();
      if (unit === 'hours' || unit === 'hour') {
        effortUnit = 'Hours';
      } else if (unit === 'days' || unit === 'day') {
        effortUnit = 'Days';
      } else if (unit === 'minutes' || unit === 'minute') {
        effortUnit = 'Minutes';
      }
      
      setEffortValue(effortValue);
      setEffortUnit(effortUnit);
      
      console.log('📋 Template effort applied:', {
        effort_value: template.estimated_effort_value,
        effort_unit: template.estimated_effort_unit,
        applied_value: effortValue,
        applied_unit: effortUnit
      });
    }
    
    // Mark that a template was applied (prevents useEffect from resetting)
    templateAppliedRef.current = true;
    
    setShowTemplateModal(false);
    setSelectedTemplateId(template.id);
    
    console.log('📋 Template applied:', {
      name: template.name,
      description: template.description?.substring(0, 50) + '...',
      estimated_effort_value: template.estimated_effort_value,
      estimated_effort_unit: template.estimated_effort_unit,
      due_date_offset: template.due_date_offset,
      calculatedDueDate: dueDate,
      subtasksCount: template.subtasks?.length || 0,
      checklistCount: template.checklist_items?.length || 0
    });
  };

  // Close quick edit dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (quickEditSubtaskId && !e.target.closest('.relative')) {
        setQuickEditSubtaskId(null);
        setQuickEditField(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [quickEditSubtaskId]);

  // Listen for save and close keyboard shortcut event
  useEffect(() => {
    const handleSaveAndClose = async () => {
      if (!isOpen) return;
      
      try {
        // Ensure status is a string (extract name if it's an object)
        const statusValue = typeof formData.status === 'object' 
          ? (formData.status?.name || formData.status?.label || formData.status)
          : formData.status;
        
        // Save the task - use _keyboardShortcut flag to skip alert and let onSave handle navigation
        const submitData = { ...formData, status: statusValue, _keyboardShortcut: true };
        await saveTask(submitData);
        // Note: onClose is called by onSave for non-autosave calls, so we don't need to call it here
        console.log('✅ Task saved and closed via keyboard shortcut');
      } catch (error) {
        console.error('Failed to save and close:', error);
      }
    };
    
    const handleSaveTask = async () => {
      if (!isOpen) return;
      
      try {
        // Ensure status is a string (extract name if it's an object)
        const statusValue = typeof formData.status === 'object' 
          ? (formData.status?.name || formData.status?.label || formData.status)
          : formData.status;
        
        // Save the task (without closing)
        const submitData = { ...formData, status: statusValue, _autoSave: true };
        await saveTask(submitData);
        console.log('✅ Task saved via keyboard shortcut');
      } catch (error) {
        console.error('Failed to save task:', error);
      }
    };
    
    const handleMarkComplete = async () => {
      if (!isOpen || !task?.id) return;
      
      try {
        // Get the last stage (completion stage)
        // Stages can be either objects {id, name, ...} or strings
        const lastStageItem = stages.length > 0 ? stages[stages.length - 1] : null;
        const lastStageName = lastStageItem 
          ? (typeof lastStageItem === 'object' ? lastStageItem.name : lastStageItem)
          : 'Complete';
        const lastStageId = lastStageItem && typeof lastStageItem === 'object' 
          ? lastStageItem.id 
          : null;
        
        // Update form data with completion status and explicitly set is_complete
        const submitData = { 
          ...formData, 
          status: lastStageName,
          is_complete: true,  // Explicitly mark as complete
          _keyboardShortcut: true  // Skip alert and let onSave handle navigation
        };
        
        // Set stage_id to last stage if available, otherwise let backend look it up
        if (lastStageId) {
          submitData.stage_id = lastStageId;
        } else {
          delete submitData.stage_id;
        }
        
        console.log('🔄 Marking task complete:', { 
          status: lastStageName, 
          stage_id: lastStageId, 
          is_complete: true 
        });
        
        await saveTask(submitData);
        console.log('✅ Task marked complete via keyboard shortcut');
        // Note: onClose is called by onSave for non-autosave calls, so we don't need to call it here
      } catch (error) {
        console.error('Failed to mark task complete:', error);
      }
    };
    
    window.addEventListener('saveAndCloseTask', handleSaveAndClose);
    window.addEventListener('saveTask', handleSaveTask);
    window.addEventListener('markTaskComplete', handleMarkComplete);
    return () => {
      window.removeEventListener('saveAndCloseTask', handleSaveAndClose);
      window.removeEventListener('saveTask', handleSaveTask);
      window.removeEventListener('markTaskComplete', handleMarkComplete);
    };
  }, [isOpen, formData, saveTask, onClose, task, stages]);

  useEffect(() => {
    // Load task categories ONCE when modal opens
    if (isOpen && !categoriesLoadedRef.current) {
      categoriesLoadedRef.current = true;
      fetchTaskCategories();
      // Only fetch templates for new tasks (not editing)
      if (!task) {
        fetchTaskTemplates();
      }
    }
    
    // Reset all refs when modal closes
    if (!isOpen) {
      categoriesLoadedRef.current = false;
      lastLoadedTaskIdRef.current = null;
      lastFormDataTaskIdRef.current = null;
      templateAppliedRef.current = false; // Reset template applied flag
      createFormInitializedRef.current = false;
      return;
    }
    
    const defaultStatus = stages.length > 0 ? stages[0] : 'To Do';
    
    if (task) {
      // Only set form data if this is a new/different task
      if (task.id !== lastFormDataTaskIdRef.current) {
        lastFormDataTaskIdRef.current = task.id;
        
        setFormData({
          id: task.id || null,
          project_id: task.project_id || null,
          stage_id: task.stage_id || null,
          name: task.name || '',
          description: task.description || '',
          workspace_content: task.workspace_content || '',
          workspace_updated_at: task.workspace_updated_at || null,
          workspace_updated_by: task.workspace_updated_by || null,
          priority: task.priority || 'Normal',
          status: task.status || defaultStatus,
          due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
          due_time: task.due_time || '',
          start_date: task.start_date ? new Date(task.start_date).toISOString().split('T')[0] : '',
          category: task.category || '',
          assignees: task.assignees || [],
          tags: task.tags || [],
          is_milestone: task.is_milestone || false,
          recurring_rule: task.recurring_rule || '',
          is_recurring_template: task.is_recurring_template || false,
          recurring_due_time: task.recurring_due_time || '',
          recurring_start_time: task.recurring_start_time || '',
          review_notes: task.review_notes || '',
          deliverable_links: task.deliverable_links || [],
          deliverable_files: task.deliverable_files || [],
          template_id: task.template_id || null,
          show_in_portal: task.show_in_portal || false
        });
        
        // Parse recurring rule if it exists and generate preview
        if (task.recurring_rule) {
          parseRecurringRuleToUI(task.recurring_rule);
          setRecurringPreview(generateRecurringPreview(task.recurring_rule));
        } else {
          setRecurringPreview('');
        }
        
        // Use estimated_effort_value and estimated_effort_unit directly from API
        if (task.estimated_effort_value !== null && task.estimated_effort_value !== undefined && task.estimated_effort_unit) {
          console.log('🔍 Using effort fields:', task.estimated_effort_value, task.estimated_effort_unit);
          setEffortValue(task.estimated_effort_value);
          // Capitalize first letter for dropdown (api returns 'hours', dropdown expects 'Hours')
          const unitCapitalized = task.estimated_effort_unit.charAt(0).toUpperCase() + task.estimated_effort_unit.slice(1);
          setEffortUnit(unitCapitalized);
        } else {
          // No effort set - clear the fields
          setEffortValue('');
          setEffortUnit('Hours');
        }
      }
      
      // Load comments, subtasks, attachments, and deliverables ONLY if task.id has changed
      if (task.id && task.id !== lastLoadedTaskIdRef.current) {
        lastLoadedTaskIdRef.current = task.id;
        loadTaskComments(task.id);
        loadTaskSubtasks(task.id);
        loadTaskAttachments(task.id);
        loadTaskDeliverables(task.id);
        loadTaskDependencies(task.id);
      }
    } else {
      // Reset form for new task ONLY if no template was just applied
      if (templateAppliedRef.current) {
        // Template was just applied, don't reset
        console.log('📋 Skipping form reset - template was just applied');
        return;
      }
      
      if (createFormInitializedRef.current) {
        // Avoid clearing in-progress create form if stages refresh
        return;
      }
      createFormInitializedRef.current = true;

      lastFormDataTaskIdRef.current = null;
      lastLoadedTaskIdRef.current = null;
      
      setFormData({
        name: '',
        description: '',
        workspace_content: '',
        workspace_updated_at: null,
        workspace_updated_by: null,
        priority: 'Normal',
        status: defaultStatus,
        due_date: '',
        due_time: '',
        estimated_effort_value: '',
        estimated_effort_unit: 'hours',
        category: '',
        assignees: [],
        tags: [],
        is_milestone: false,
        review_notes: '',
        deliverable_links: [],
        deliverable_files: []
      });
      setComments([]);
      setSubtasks([]);
      setPendingSubtasks([]); // Clear pending subtasks
      setAttachments([]);
      setPendingAttachments([]); // Clear pending attachments
      setDeliverables([]);
      setDependencies({ blocking: [], blocked_by: [], is_blocked: false }); // Clear dependencies
      
      // Reset structured effort fields
      setEffortValue('');
      setEffortUnit('Minutes');
    }
  }, [task?.id, stages, isOpen]); // Only depend on task.id, not entire task object

  const clearAssigneesUpdatedFlag = () => {
    setFormData(prev => (prev?.assignees_updated ? { ...prev, assignees_updated: false } : prev));
  };

  async function saveTask(submitData) {
    if (!onSave || typeof onSave !== 'function') {
      console.error('❌ saveTask failed - onSave is not a function:', onSave);
      return null;
    }

    const savedTask = await onSave(submitData);

    // If this is a newly-created parent task from a template, persist pending subtasks once.
    if (
      savedTask?.id &&
      !task?.id &&
      !submitData?._autoSave &&
      Array.isArray(pendingSubtasks) &&
      pendingSubtasks.length > 0
    ) {
      try {
        for (const pendingSubtask of pendingSubtasks) {
          const normalizedEffortValue = (() => {
            if (pendingSubtask?.estimated_effort_value === null || pendingSubtask?.estimated_effort_value === undefined) return null;
            const raw = String(pendingSubtask.estimated_effort_value).trim();
            if (!raw) return null;
            const parsed = parseInt(raw, 10);
            return Number.isNaN(parsed) ? null : parsed;
          })();

          const normalizedEffortUnit = (() => {
            if (!pendingSubtask?.estimated_effort_unit) return null;
            const unit = String(pendingSubtask.estimated_effort_unit).trim().toLowerCase();
            return unit || null;
          })();

          await api.post(`/tasks/${savedTask.id}/subtasks`, {
            name: pendingSubtask.name,
            description: pendingSubtask.description || '',
            status: pendingSubtask.status || 'To Do',
            priority: pendingSubtask.priority || 'Normal',
            estimated_effort_value: normalizedEffortValue,
            estimated_effort_unit: normalizedEffortUnit,
            estimated_effort_minutes: effortToMinutes(normalizedEffortValue, normalizedEffortUnit)
          });
        }

        setPendingSubtasks([]);
        loadTaskSubtasks(savedTask.id);
      } catch (subtaskError) {
        console.error('Failed to create template subtasks after parent save:', subtaskError);
        alert('Task was created, but one or more template subtasks failed to create.');
      }
    }

    if (submitData?.assignees_updated) {
      clearAssigneesUpdatedFlag();
    }
    return savedTask;
  }

  // Auto-save function
  const triggerAutoSave = useCallback(async () => {
    // Don't auto-save if modal is not open
    if (!isOpen) {
      console.log('⏸️ Auto-save skipped - modal not open');
      return null;
    }
    
    // Get the effective task ID (from task prop or formData)
    const effectiveTaskId = task?.id || formData.id;
    
    // For new tasks, require at least a name
    if (!effectiveTaskId && !formData.name?.trim()) {
      console.log('⏸️ Auto-save skipped - no task name');
      return null;
    }
    
    // Check if onSave is defined
    if (!onSave || typeof onSave !== 'function') {
      console.error('❌ Auto-save failed - onSave is not a function:', onSave);
      return null;
    }
    
    try {
      setIsSaving(true);
      setHasUnsavedChanges(false);
      
      console.log('🚀 Starting auto-save for task:', effectiveTaskId ? `existing (${effectiveTaskId})` : 'new');
      
      // Prepare data for save
      const estimatedEffortMinutes = effortToMinutes(effortValue, effortUnit);
      
      // Ensure status is a string (extract name if it's an object)
      const statusValue = typeof formData.status === 'object' 
        ? (formData.status?.name || formData.status?.label || formData.status)
        : formData.status;
      
      const submitData = {
        ...formData,
        id: effectiveTaskId, // Ensure we use the effective task ID
        status: statusValue,
        // Send structured effort fields
        estimated_effort_value: effortValue ? parseInt(effortValue, 10) : null,
        estimated_effort_unit: effortUnit ? effortUnit.toLowerCase() : null,
        estimated_effort_minutes: estimatedEffortMinutes,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        _autoSave: true // Flag to indicate this is an auto-save
      };
      
      // Call the onSave prop
      const savedTask = await saveTask(submitData);
      
      // If this was a new task and now has an ID, update formData with the ID
      // This enables subsequent auto-saves to work correctly
      if (!task?.id && !formData.id && savedTask?.id) {
        setFormData(prev => ({ ...prev, id: savedTask.id }));
        console.log('🆔 Updated formData with new task ID:', savedTask.id);
      }
      
      // If this was a new task and now has an ID, upload pending attachments
      if (!task?.id && savedTask?.id && pendingAttachments.length > 0) {
        const token = localStorage.getItem('access_token');
        
        for (const file of pendingAttachments) {
          const formData = new FormData();
          formData.append('file', file);
          
          await api.post(`/tasks/${savedTask.id}/attachments`, formData, {
            headers: { 
              'Content-Type': 'multipart/form-data'
            }
          });
        }
        setPendingAttachments([]);
      }
      
      setLastSaved(new Date());
      setIsSaving(false);
      
      console.log('✅ Auto-save completed');
      return savedTask;
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
      setIsSaving(false);
      setHasUnsavedChanges(true);
      return null;
    }
  }, [isOpen, task?.id, formData, effortValue, effortUnit, saveTask, pendingAttachments, effortToMinutes]);
  
  // Auto-save on form changes (debounced for existing tasks)
  useEffect(() => {
    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    // Don't trigger on initial load or if no changes
    if (!hasUnsavedChanges) return;
    
    // For existing tasks OR tasks that have an ID in formData: debounce auto-save by 2 seconds
    // formData.id is set after the first auto-save creates the task
    if (task?.id || formData.id) {
      const timeout = setTimeout(() => {
        triggerAutoSave();
      }, 2000);
      
      setAutoSaveTimeout(timeout);
      
      return () => clearTimeout(timeout);
    }
  }, [formData, effortValue, effortUnit, hasUnsavedChanges, task?.id]);
  
  // Track changes to formData to set hasUnsavedChanges
  useEffect(() => {
    // Only mark as unsaved if task exists or name is filled
    if (task?.id || formData.name?.trim()) {
      setHasUnsavedChanges(true);
    }
  }, [formData, effortValue, effortUnit]);


  const mergeOptimisticComments = (serverComments) => {
    const optimistic = comments.filter(c => c.status === 'sending' || c.status === 'failed');
    if (!optimistic.length) {
      return serverComments;
    }

    const merged = [...serverComments];
    const usedServerIds = new Set();

    optimistic.forEach(opt => {
      const optTime = opt.created_at ? new Date(opt.created_at).getTime() : null;
      const matchIndex = serverComments.findIndex((c, idx) => {
        if (usedServerIds.has(c.id)) return false;
        if (opt.client_message_id && c.client_message_id && opt.client_message_id === c.client_message_id) {
          return true;
        }
        const sameContent = (c.content || '') === (opt.content || '');
        const sameUser = String(c.user_id || '') === String(opt.user_id || '');
        const sameParent = String(c.parent_comment_id || '') === String(opt.parent_comment_id || '');
        if (!sameContent || !sameUser || !sameParent) return false;
        if (!optTime || !c.created_at) return true;
        const serverTime = new Date(c.created_at).getTime();
        return Math.abs(serverTime - optTime) <= 10000;
      });

      if (matchIndex >= 0) {
        usedServerIds.add(serverComments[matchIndex].id);
        merged[matchIndex] = { ...serverComments[matchIndex] };
      } else {
        merged.push(opt);
      }
    });

    return merged;
  };

  const loadTaskComments = async (taskId) => {
    if (!taskId) return;
    
    // Prevent duplicate calls
    if (loadTaskComments.loading) return;
    loadTaskComments.loading = true;
    
    try {
      const response = await api.get(`/tasks/${taskId}/comments`);
      
      // Handle both old format (array) and new format (object with comments array)
      if (response.data && Array.isArray(response.data)) {
        setComments(mergeOptimisticComments(response.data));
      } else if (response.data && response.data.comments && Array.isArray(response.data.comments)) {
        setComments(mergeOptimisticComments(response.data.comments));
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
      setComments([]);
    } finally {
      loadTaskComments.loading = false;
    }
  };

  // Function to scroll comments to bottom
  const scrollCommentsToBottom = () => {
    if (commentsContainerRef.current) {
      commentsContainerRef.current.scrollTop = commentsContainerRef.current.scrollHeight;
    }
  };

  // Auto-scroll to bottom when comments change or task opens
  useEffect(() => {
    if (comments.length > 0 && task?.id) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollCommentsToBottom, 100);
    }
  }, [comments.length, task?.id]);

  const loadTaskSubtasks = async (taskId) => {
    if (!taskId) return;
    
    // Prevent duplicate calls
    if (loadTaskSubtasks.loading) return;
    loadTaskSubtasks.loading = true;
    
    try {
      const response = await api.get(`/tasks/${taskId}/subtasks`);
      setSubtasks([...response.data || []]); // Force new array reference to trigger re-render
    } catch (error) {
      console.error('Failed to load subtasks:', error);
    } finally {
      loadTaskSubtasks.loading = false;
    }
  };


  const handleCreateSubtask = async () => {
    if (!newSubtaskData.name.trim()) {
      alert('Subtask name is required');
      return;
    }

    const normalizedEffortValue = (() => {
      if (newSubtaskData.estimated_effort_value === null || newSubtaskData.estimated_effort_value === undefined) {
        return null;
      }
      const raw = String(newSubtaskData.estimated_effort_value).trim();
      if (!raw) return null;
      const parsed = parseInt(raw, 10);
      return Number.isNaN(parsed) ? null : parsed;
    })();

    const normalizedEffortUnit = (() => {
      if (!newSubtaskData.estimated_effort_unit) return null;
      const unit = String(newSubtaskData.estimated_effort_unit).trim().toLowerCase();
      return unit || null;
    })();

    const normalizedSubtaskData = {
      ...newSubtaskData,
      estimated_effort_value: normalizedEffortValue,
      estimated_effort_unit: normalizedEffortUnit,
      estimated_effort_minutes: effortToMinutes(normalizedEffortValue, normalizedEffortUnit)
    };

    // If task doesn't have an ID yet (new task), auto-save it first
    if (!task || !task.id) {
      // Validate parent task has required fields
      if (!formData.name || !formData.name.trim()) {
        alert('Please enter a task name before adding subtasks');
        return;
      }

      try {
        // Close modal and reopen with saved task to preserve all state
        const submitData = { ...formData };
        
        // Save the parent task
        const savedTask = await saveTask(submitData);
        
        if (!savedTask || !savedTask.id) {
          alert('Failed to save parent task. Please try saving manually first.');
          return;
        }
        
        // Close the current modal
        onClose();
        
        // Give a moment for the modal to close, then reopen with the saved task
        setTimeout(async () => {
          // The parent component should refetch tasks, which will include our new task
          // Then we can open it and add the subtask
          try {
            // Create the subtask now that we have the parent task ID
            await api.post(`/tasks/${savedTask.id}/subtasks`, normalizedSubtaskData);
            
            // Reopen the parent task for editing
            if (onEditSubtask) {
              onEditSubtask(savedTask);
            }
          } catch (error) {
            console.error('Failed to create subtask:', error);
            alert('Parent task saved, but failed to create subtask. Please open the task and try again.');
          }
        }, 300);
        
        return;
      } catch (error) {
        console.error('Failed to auto-save task and create subtask:', error);
        alert('Failed to save task. Please try again.');
      }
      return;
    }

    // For existing tasks, create subtask via API
    try {
      await api.post(`/tasks/${task.id}/subtasks`, normalizedSubtaskData);
      
      // Reset form and reload subtasks
      setNewSubtaskData({
        name: '',
        description: '',
        priority: 'Normal',
        status: 'To Do',
        assignees: [],
        due_date: '',
        estimated_effort_value: '',
        estimated_effort_unit: 'hours'
      });
      setShowSubtaskForm(false);
      loadTaskSubtasks(task.id);
    } catch (error) {
      console.error('Failed to create subtask:', error);
      alert('Failed to create subtask. Please try again.');
    }
  };

  const handleUpdateSubtask = async (subtaskId, updateData) => {
    try {
      const response = await api.put(`/tasks/${subtaskId}`, updateData);
      
      loadTaskSubtasks(task.id);
    } catch (error) {
      console.error('Failed to update subtask:', error);
      alert('Failed to update subtask. Please try again.');
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    if (!confirm('Are you sure you want to delete this subtask?')) return;
    
    try {
      await api.delete(`/tasks/${subtaskId}`);
      
      loadTaskSubtasks(task.id);
    } catch (error) {
      console.error('Failed to delete subtask:', error);
      alert('Failed to delete subtask. Please try again.');
    }
  };

  const handleSubtaskStatusToggle = async (subtask) => {
    if (!task?.id) {
      alert('Please save the parent task first');
      return;
    }
    
    try {
      const getStageName = (stage) => (stage && typeof stage === 'object' ? stage.name : stage);
      const lastStageName = stages.length > 0 ? getStageName(stages[stages.length - 1]) : 'Done';
      const firstStageName = stages.length > 0 ? getStageName(stages[0]) : 'To Do';
      
      // If currently in last stage, move to first stage (uncomplete)
      // Otherwise, move to last stage (complete)
      const completionStatuses = [lastStageName, 'Done', 'Complete', 'Completed'];
      const isComplete = completionStatuses.includes(subtask.status);
      const newStatus = isComplete ? firstStageName : lastStageName;

      // Optimistic UI update
      setSubtasks(prev => prev.map(s => 
        s.id === subtask.id ? { ...s, status: newStatus } : s
      ));

      const matchingStage = stages.find(s => (s?.name || s) === newStatus);
      const stageId = matchingStage && typeof matchingStage === 'object' ? matchingStage.id : null;
      const updatePayload = { status: newStatus };
      if (stageId) {
        updatePayload.stage_id = stageId;
      }

      await handleUpdateSubtask(subtask.id, updatePayload);
    } catch (error) {
      console.error('❌ Failed to toggle subtask status:', error);
      alert('Failed to update subtask. Please try again.');
    }
  };

  const handleQuickUpdateSubtask = async (subtaskId, field, value) => {
    try {
      const subtask = subtasks.find(s => s.id === subtaskId);
      if (!subtask) return;
      
      const updateData = { ...subtask, [field]: value };
      await handleUpdateSubtask(subtaskId, updateData);
      setQuickEditSubtaskId(null);
      setQuickEditField(null);
    } catch (error) {
      console.error('Quick update failed:', error);
    }
  };

  // Helper function to get member name by ID
  const getMemberName = (memberId) => {
    // teamMembers structure: [{user: {id, name, email}}]
    const member = teamMembers?.find(m => m.user?.id === memberId || m.id === memberId);
    if (!member) return null;
    
    // Try user.name first (team member structure), then direct properties
    if (member.user) {
      return member.user.name || member.user.email || member.user.username;
    }
    return member.name || member.full_name || member.display_name || member.email || member.username;
  };

  // Helper function to get assignee names as string
  const getAssigneeNames = (assigneeIds) => {
    if (!assigneeIds || assigneeIds.length === 0) return null;
    const names = assigneeIds.map(id => getMemberName(id)).filter(Boolean);
    return names.length > 0 ? names.join(', ') : null;
  };


  const loadTaskAttachments = async (taskId) => {
    if (!taskId) return;
    
    // Prevent duplicate calls
    if (loadTaskAttachments.loading) return;
    loadTaskAttachments.loading = true;
    
    try {
      const response = await api.get(`/tasks/${taskId}/attachments`);
      // Add task_id to each attachment for proxy URL construction
      const attachmentsWithTaskId = (response.data || []).map(att => ({
        ...att,
        task_id: taskId
      }));
      setAttachments(attachmentsWithTaskId);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    } finally {
      loadTaskAttachments.loading = false;
    }
  };

  const loadTaskDeliverables = async (taskId) => {
    if (!taskId) return;
    
    // Prevent duplicate calls
    if (loadTaskDeliverables.loading) return;
    loadTaskDeliverables.loading = true;
    
    try {
      const response = await api.get(`/tasks/${taskId}/deliverables`);
      setDeliverables(response.data || []);
    } catch (error) {
      console.error('Failed to load deliverables:', error);
      setDeliverables([]);
    } finally {
      loadTaskDeliverables.loading = false;
    }
  };

  // Load task dependencies
  const loadTaskDependencies = async (taskId) => {
    if (!taskId) return;
    
    try {
      const response = await api.get(`/tasks/${taskId}/dependencies`);
      if (response.data.success) {
        setDependencies({
          blocking: response.data.blocking || [],
          blocked_by: response.data.blocked_by || [],
          is_blocked: response.data.is_blocked || false,
          unresolved_blocker_count: response.data.unresolved_blocker_count || 0
        });
      }
    } catch (error) {
      console.error('Failed to load dependencies:', error);
      setDependencies({ blocking: [], blocked_by: [], is_blocked: false });
    }
  };

  // Search for tasks to add as dependencies
  const searchTasksForDependency = async (searchTerm) => {
    if (!task?.id) return;
    
    setSearchingDependencies(true);
    try {
      const params = new URLSearchParams({
        exclude_task_id: task.id,
        search: searchTerm,
        limit: '10'
      });
      
      // Filter by same project if task has a project
      if (task.project_id) {
        params.append('project_id', task.project_id);
      }
      
      const response = await api.get(`/tasks/search-for-dependency?${params}`);
      if (response.data.success) {
        setDependencySearchResults(response.data.tasks || []);
      }
    } catch (error) {
      console.error('Failed to search tasks:', error);
      setDependencySearchResults([]);
    } finally {
      setSearchingDependencies(false);
    }
  };

  // Add a dependency
  const handleAddDependency = async (dependencyTaskId, type) => {
    if (!task?.id || !dependencyTaskId) return;
    
    setAddingDependency(true);
    try {
      await api.post(`/tasks/${task.id}/dependencies`, {
        dependency_task_id: dependencyTaskId,
        type: type // 'blocked_by' or 'blocks'
      });
      
      // Reload dependencies
      loadTaskDependencies(task.id);
      setShowAddDependencyDropdown(false);
      setDependencySearchTerm('');
      setDependencySearchResults([]);
    } catch (error) {
      console.error('Failed to add dependency:', error);
      alert(error.response?.data?.detail || 'Failed to add dependency');
    } finally {
      setAddingDependency(false);
    }
  };

  // Remove a dependency
  const handleRemoveDependency = async (dependencyId) => {
    if (!task?.id || !dependencyId) return;
    
    try {
      await api.delete(`/tasks/${task.id}/dependencies/${dependencyId}`);
      loadTaskDependencies(task.id);
    } catch (error) {
      console.error('Failed to remove dependency:', error);
      alert('Failed to remove dependency');
    }
  };

  // Debounced search for dependencies
  useEffect(() => {
    const timer = setTimeout(() => {
      if (dependencySearchTerm.length >= 2) {
        searchTasksForDependency(dependencySearchTerm);
      } else {
        setDependencySearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [dependencySearchTerm, task?.id]);

  const handleAddDeliverableLink = async () => {
    if (!newDeliverableLink.trim() || !task?.id) return;

    try {
      await api.post(`/tasks/${task.id}/deliverables`, {
        type: 'link',
        file_url: newDeliverableLink,
        title: newDeliverableTitle || newDeliverableLink,
        is_public: true
      });

      setNewDeliverableLink('');
      setNewDeliverableTitle('');
      setShowDeliverableForm(false);
      loadTaskDeliverables(task.id);
    } catch (error) {
      console.error('Failed to add deliverable link:', error);
    }
  };

  const handleUploadDeliverableFile = async (file, title = '') => {
    if (!file || !task?.id) return;

    setUploadingDeliverable(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);
      formData.append('is_public', 'true');

      await api.post(`/tasks/${task.id}/deliverables/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });

      loadTaskDeliverables(task.id);
    } catch (error) {
      console.error('Failed to upload deliverable file:', error);
    } finally {
      setUploadingDeliverable(false);
    }
  };

  const handleDeleteDeliverable = async (deliverableId) => {
    if (!task?.id) return;

    const confirmed = window.confirm(
      '⚠️ WARNING: This will permanently delete this deliverable and ALL associated comments.\n\n' +
      'If you just want to update the file, use "Replace" instead.\n\n' +
      'Are you sure you want to delete?'
    );
    
    if (!confirmed) return;

    try {
      await api.delete(`/deliverables/${deliverableId}`);

      loadTaskDeliverables(task.id);
    } catch (error) {
      console.error('Failed to delete deliverable:', error);
      alert('Failed to delete deliverable: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReplaceDeliverableFile = async (deliverableId) => {
    // Create hidden file input
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
    
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const confirmed = window.confirm(
        `Replace file with "${file.name}"?\n\n` +
        'This will keep all comments and conversation history.'
      );
      
      if (!confirmed) return;
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await api.post(
          `/deliverables/${deliverableId}/replace-file`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        alert('✅ File replaced successfully!');
        loadTaskDeliverables(task.id);
      } catch (error) {
        console.error('Failed to replace file:', error);
        alert('Failed to replace file: ' + (error.response?.data?.detail || error.message));
      }
    };
    
    fileInput.click();
  };

  const handleEditDeliverable = (deliverable) => {
    setEditingDeliverable(deliverable);
    setEditDeliverableData({
      title: deliverable.title || '',
      description: deliverable.description || '',
      review_notes: deliverable.review_notes || '',
      status: deliverable.status || 'pending'
    });
  };

  const handleUpdateDeliverable = async () => {
    if (!editingDeliverable?.id || !task?.id) {
      return;
    }

    try {
      const response = await api.put(`/deliverables/${editingDeliverable.id}`, editDeliverableData);

      setEditingDeliverable(null);
      setEditDeliverableData({ title: '', description: '', review_notes: '', status: 'pending' });
      
      await loadTaskDeliverables(task.id);
    } catch (error) {
      console.error('Failed to update deliverable:', error);
      alert('Failed to update deliverable: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddComment = async (parentCommentId = null, options = {}) => {
    const overrideContent = options.content;
    const commentText = overrideContent != null ? overrideContent : (parentCommentId ? replyText : newComment);
    if (!commentText.trim()) return;

    setIsCommentSending(true);
    
    const now = Date.now();
    if (commentSendLockRef.current || now - lastCommentSendAtRef.current < 1000) {
      setIsCommentSending(false);
      return;
    }

    commentSendLockRef.current = true;
    lastCommentSendAtRef.current = now;
    setIsCommentSending(true);

    const clientMessageId = options.clientMessageId || ((window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `cm_${Date.now()}_${Math.random().toString(16).slice(2)}`);
    const optimisticId = options.optimisticId || `optimistic-${clientMessageId}`;
    const optimisticCreatedAt = new Date().toISOString();

    const optimisticAttachments = options.attachments || commentAttachments;

    let effectiveTaskId = task?.id || formData.id;

    if (options.useExistingOptimistic) {
      setComments(prev => prev.map(comment => {
        if (comment.optimistic_id === optimisticId || comment.client_message_id === clientMessageId) {
          return {
            ...comment,
            status: 'sending',
            updated_at: optimisticCreatedAt
          };
        }
        return comment;
      }));
    } else {
      const optimisticComment = {
        id: optimisticId,
        optimistic_id: optimisticId,
        client_message_id: clientMessageId,
        task_id: effectiveTaskId,
        user_id: user?.id,
        user_name: user?.name,
        user_email: user?.email,
        user_avatar_url: user?.avatar_url || user?.profile_image_url || '',
        content: commentText,
        parent_comment_id: parentCommentId,
        mentions: [],
        attachments: optimisticAttachments,
        created_at: optimisticCreatedAt,
        updated_at: optimisticCreatedAt,
        status: 'sending'
      };

      setComments(prev => [...prev, optimisticComment]);
    }

    if (!options.preserveInput) {
      if (parentCommentId) {
        setReplyText('');
        setReplyingTo(null);
      } else {
        setNewComment('');
        setCommentAttachments([]);
      }
    }

    try {
      if (!effectiveTaskId) {
        if (!formData.name?.trim()) {
          throw new Error('Task name required before commenting');
        }

        const savedTask = await triggerAutoSave();
        effectiveTaskId = savedTask?.id || formData.id || task?.id;
      }

      if (!effectiveTaskId) {
        throw new Error('Task ID not available after auto-save');
      }

      // Extract mentions from comment text
      const mentionRegex = /@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(commentText)) !== null) {
        // Find user by name
        const mentionedUser = teamMembers.find(member => 
          member.user.name.toLowerCase().includes(match[1].toLowerCase())
        );
        if (mentionedUser) {
          mentions.push(mentionedUser.user.id);
        }
      }
      
      const response = await api.post(`/tasks/${effectiveTaskId}/comments`, {
        content: commentText,
        parent_comment_id: parentCommentId,
        mentions: mentions,
        attachments: optimisticAttachments,
        client_message_id: clientMessageId
      });
      
      const persistedComment = { ...response.data, client_message_id: clientMessageId };
      setComments(prev => {
        const existingIndex = prev.findIndex(c =>
          c.optimistic_id === optimisticId || c.client_message_id === clientMessageId
        );
        if (existingIndex === -1) {
          return [...prev, persistedComment];
        }
        const next = [...prev];
        next[existingIndex] = { ...persistedComment };
        return next;
      });
    } catch (error) {
      console.error('Failed to add comment:', error);
      setComments(prev => prev.map(comment => {
        if (comment.optimistic_id === optimisticId || comment.client_message_id === clientMessageId) {
          return { ...comment, status: 'failed' };
        }
        return comment;
      }));
    } finally {
      setIsCommentSending(false);
      commentSendLockRef.current = false;
    }
  };

  const handleRetryComment = (comment) => {
    if (!comment) return;
    handleAddComment(comment.parent_comment_id || null, {
      content: comment.content,
      clientMessageId: comment.client_message_id,
      optimisticId: comment.optimistic_id || comment.id,
      attachments: comment.attachments || [],
      preserveInput: true,
      useExistingOptimistic: true
    });
  };

  const handleStartReply = (commentId) => {
    setReplyingTo(commentId);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  // Handle comment text change with mention detection
  const handleCommentTextChange = (e) => {
    const text = e.target.value;
    setNewComment(text);
    
    // Check for @ mention
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = text.substring(0, cursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbol !== -1 && lastAtSymbol === cursorPosition - 1) {
      // Just typed @, show all team members
      setMentionSearchTerm('');
      setShowMentionSuggestions(true);
      
      // Calculate position for dropdown
      const textarea = e.target;
      const rect = textarea.getBoundingClientRect();
      setMentionPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    } else if (lastAtSymbol !== -1) {
      // Typing after @
      const searchTerm = textBeforeCursor.substring(lastAtSymbol + 1);
      if (searchTerm && !searchTerm.includes(' ')) {
        setMentionSearchTerm(searchTerm);
        setShowMentionSuggestions(true);
      } else if (searchTerm.includes(' ')) {
        setShowMentionSuggestions(false);
      }
    } else {
      setShowMentionSuggestions(false);
    }
  };

  // Insert mention into comment
  const insertMention = (userName) => {
    const cursorPosition = newComment.lastIndexOf('@');
    const beforeMention = newComment.substring(0, cursorPosition);
    const afterMention = newComment.substring(cursorPosition + mentionSearchTerm.length + 1);
    setNewComment(`${beforeMention}@${userName} ${afterMention}`);
    setShowMentionSuggestions(false);
  };

  // Handle comment file upload
  const handleCommentFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingCommentFile(true);
    try {
      const uploadedFiles = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/upload-comment-attachment', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedFiles.push({
          name: file.name,
          url: response.data.url,
          size: file.size,
          type: file.type
        });
      }

      setCommentAttachments([...commentAttachments, ...uploadedFiles]);
    } catch (error) {
      console.error('Failed to upload comment attachment:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingCommentFile(false);
    }
  };

  // Remove comment attachment
  const removeCommentAttachment = (index) => {
    setCommentAttachments(commentAttachments.filter((_, i) => i !== index));
  };

  // Handle Enter key to send
  const handleCommentKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isCommentSending && !uploadingCommentFile) {
        handleAddComment();
      }
    }
  };

  // Helper function to organize comments into nested structure
  const organizeComments = (comments) => {
    const commentMap = {};
    const rootComments = [];

    // First pass: create comment map
    comments.forEach(comment => {
      commentMap[comment.id] = { ...comment, replies: [] };
    });

    // Second pass: organize into hierarchy
    comments.forEach(comment => {
      if (comment.parent_comment_id && commentMap[comment.parent_comment_id]) {
        commentMap[comment.parent_comment_id].replies.push(commentMap[comment.id]);
      } else {
        rootComments.push(commentMap[comment.id]);
      }
    });

    return rootComments;
  };

  const handleFileUpload = async (file, isCommentAttachment = false, commentId = null) => {
    if (!file) return;

    // If task doesn't have an ID yet (new task), store file temporarily
    if (!task || !task.id) {
      setPendingAttachments([...pendingAttachments, file]);
      setAttachments([...attachments, {
        filename: file.name,
        file_size: file.size,
        content_type: file.type,
        id: `temp-${Date.now()}`
      }]);
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const endpoint = isCommentAttachment 
        ? `/comments/${commentId}/attachments`
        : `/tasks/${task.id}/attachments`;

      await api.post(endpoint, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (task?.id) {
        loadTaskAttachments(task.id);
      }
    } catch (error) {
      console.error('❌ Failed to upload file:', error);
      alert(`Failed to upload file: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteAttachment = async (attachmentIndex) => {
    if (!task?.id) return;
    
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      await api.delete(`/tasks/${task.id}/attachments/${attachmentIndex}`);

      loadTaskAttachments(task.id);
    } catch (error) {
      console.error('❌ Failed to delete attachment:', error);
      alert(`Failed to delete attachment: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any pending auto-save
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
      setAutoSaveTimeout(null);
    }
    
    // Convert structured effort to minutes for storage
    const estimatedEffortMinutes = effortToMinutes(effortValue, effortUnit);
    console.log('💾 SAVE - effortValue:', effortValue, 'effortUnit:', effortUnit, '→ minutes:', estimatedEffortMinutes);
    
    // Ensure status is a string (extract name if it's an object)
    const statusValue = typeof formData.status === 'object' 
      ? (formData.status?.name || formData.status?.label || formData.status)
      : formData.status;
    
    const submitData = {
      ...formData,
      status: statusValue,
      // Send structured effort fields
      estimated_effort_value: effortValue ? parseInt(effortValue, 10) : null,
      estimated_effort_unit: effortUnit ? effortUnit.toLowerCase() : null,
      estimated_effort_minutes: estimatedEffortMinutes,
      due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      _autoSave: false // This is a manual save
    };

    // If editing an existing task, check for completion validation
    if (task && task.id) {
      // Create a temporary task object with subtasks for validation
      const taskWithSubtasks = {
        ...task,
        subtasks,
        status: task.status  // current status
      };
      
      const validation = validateTaskCompletion(taskWithSubtasks, submitData.status);
      
      if (!validation.canComplete) {
        // Store the pending save and show validation modal
        setPendingTaskSave({
          submitData,
          task: taskWithSubtasks,
          incompleteSubtasks: validation.incompleteSubtasks,
          newStatus: submitData.status,
          previousStatus: task.status
        });
        setShowSubtaskModal(true);
        return;
      }
    }

    // If validation passes or not needed, proceed with save
    const savedTask = await saveTask(submitData);
    
    // If there are pending attachments (from new task creation), upload them now
    if (pendingAttachments.length > 0 && savedTask && savedTask.id) {
      try {
        // Upload all pending attachments
        for (const file of pendingAttachments) {
          const formData = new FormData();
          formData.append('file', file);
          
          await api.post(`/tasks/${savedTask.id}/attachments`, formData, {
            headers: { 
              'Content-Type': 'multipart/form-data'
            }
          });
        }
        setPendingAttachments([]); // Clear pending attachments
      } catch (error) {
        console.error('Failed to upload attachments:', error);
        // Don't block task saving if attachments fail
      }
    }
    
    // Only close modal for manual saves (not auto-saves)
    if (!submitData._autoSave) {
      onClose();
    }
  };

  const handleSubtaskModalConfirm = async () => {
    if (!pendingTaskSave) return;

    try {
      // Mark all incomplete subtasks as complete using bulk endpoint
      const subtaskIds = pendingTaskSave.incompleteSubtasks.map(subtask => subtask.id);
      
      await api.put('/subtasks/bulk-complete', subtaskIds);
      
      // Update local subtasks state
      setSubtasks(subtasks.map(subtask =>
        pendingTaskSave.incompleteSubtasks.some(incomplete => incomplete.id === subtask.id)
          ? { ...subtask, completed: true, status: 'Done' }
          : subtask
      ));
      
      // Now proceed with task save
      await onSave(pendingTaskSave.submitData);
      
      // Clean up
      setPendingTaskSave(null);
      setShowSubtaskModal(false);
      onClose();
    } catch (error) {
      console.error('Failed to complete subtasks and save task:', error);
    }
  };

  const handleSubtaskModalCancel = () => {
    // Just clean up without saving
    setPendingTaskSave(null);
    setShowSubtaskModal(false);
  };

  const handleMentionClick = (user) => {
    setSelectedUser(user);
    setShowUserProfile(true);
  };

  const handleDescriptionChange = (content, mentions = []) => {
    setFormData({ ...formData, description: content });
  };

  const handleWorkspaceChange = (content) => {
    setFormData(prev => ({ ...prev, workspace_content: content }));
  };

  // Fetch available projects when move modal opens
  useEffect(() => {
    const fetchAvailableProjects = async () => {
      if (showMoveProjectModal && task && project) {
        try {
          const response = await api.get('/projects');
          
          // Filter to only show projects under the same client AND exclude current project
          const sameClientProjects = response.data.filter(p => 
            p.id !== task.project_id && p.client_id === project.client_id
          );
          setAvailableProjects(sameClientProjects);
        } catch (error) {
          console.error('Failed to fetch projects:', error);
        }
      }
    };
    
    fetchAvailableProjects();
  }, [showMoveProjectModal, task?.id, project?.id]);

  const handleMoveToProject = async () => {
    if (!selectedProjectId || !task) return;
    
    try {
      setMovingTask(true);
      
      const response = await api.put(`/tasks/${task.id}/move-to-project`, { destination_project_id: selectedProjectId });
      
      // Show success message
      alert(`✅ ${response.data.message}\nMoved to: ${response.data.moved_to_stage}\nSubtasks moved: ${response.data.subtasks_moved}`);
      
      // Close modals and refresh
      setShowMoveProjectModal(false);
      setSelectedProjectId('');
      onClose();
      
      // Trigger a refresh of the task list (parent component should handle this)
      window.location.reload(); // Simple refresh for now
      
    } catch (error) {
      console.error('Failed to move task:', error);
      alert(`Failed to move task: ${error.response?.data?.detail || error.message}`);
    } finally {
      setMovingTask(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-screen h-screen flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-4 flex-1">
            <div className="flex-1">
              {task?.parent_task_id && (
                <button
                  onClick={async () => {
                    try {
                      // Auto-save subtask before navigating to parent (silent save)
                      try {
                        const submitData = { ...formData, _autoSave: true };
                        await saveTask(submitData);
                      } catch (error) {
                        console.error('Failed to save subtask:', error);
                      }
                      
                      // Fetch parent task and swap directly (no close/reopen)
                      const parentTaskResponse = await api.get(`/tasks/${task.parent_task_id}`);
                      const parentTask = parentTaskResponse.data;
                      
                      if (parentTask && onEditSubtask) {
                        onEditSubtask(parentTask);
                      } else {
                        console.error('Parent task not found');
                        alert('Could not find parent task. It may have been deleted.');
                      }
                    } catch (error) {
                      console.error('Failed to load parent task:', error);
                      alert(`Failed to load parent task: ${error.message}`);
                    }
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium mb-1 flex items-center space-x-1"
                >
                  <span>←</span>
                  <span>Main Task</span>
                </button>
              )}
              <h2 className="text-2xl font-bold text-gray-900">
                {task ? (task.parent_task_id ? 'Edit Subtask' : 'Edit Task') : 'New Task'}
              </h2>
            </div>
            {!task && (
              <button
                type="button"
                onClick={() => setShowTemplateModal(true)}
                className="flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Use Template
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* Archive, Move, and Delete buttons - only show when editing existing task */}
            {task && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowMoveProjectModal(true);
                  }}
                  className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Move to another project"
                >
                  <FolderInput className="w-5 h-5 text-blue-600" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (onArchive) {
                      onArchive(task.id, task.archived);
                    }
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title={task.archived ? "Unarchive task" : "Archive task"}
                >
                  {task.archived ? (
                    <ArchiveRestore className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Archive className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.confirm('Are you sure you want to delete this task?')) {
                      if (onDelete) {
                        onDelete(task.id);
                        onClose();
                      }
                    }
                  }}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete task"
                >
                  <Trash2 className="w-5 h-5 text-red-600" />
                </button>
              </>
            )}
            {/* Auto-save indicator */}
            <div className="flex items-center mr-4">
              {isSaving && (
                <span className="text-sm text-gray-500 flex items-center">
                  <svg className="animate-spin h-4 w-4 mr-2 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              )}
              {!isSaving && lastSaved && (
                <span className="text-xs text-gray-400">
                  Saved {new Date(lastSaved).toLocaleTimeString()}
                </span>
              )}
              {!isSaving && hasUnsavedChanges && task?.id && (
                <span className="text-xs text-orange-500">
                  Unsaved changes
                </span>
              )}
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Template Selection Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Select Task Template</h3>
                <p className="text-sm text-gray-600 mt-1">Choose a template to prefill your task</p>
                <p className="text-xs text-gray-500 mt-1">Found {taskTemplates.length} templates</p>
              </div>
              
              <div className="overflow-y-auto max-h-[60vh] p-6">
                {taskTemplates.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>No task templates available</p>
                    <p className="text-sm mt-2">Create templates in Settings to use them here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {taskTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => applyTemplate(template)}
                        className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{template.name}</h4>
                            {template.description && (
                              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {template.category && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                  {template.category}
                                </span>
                              )}
                              {template.priority && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                                  {template.priority}
                                </span>
                              )}
                              {template.estimated_effort && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                  {template.estimated_effort}
                                </span>
                              )}
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Move to Project Modal */}
        {showMoveProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Move Task to Project</h3>
                <p className="text-sm text-gray-600 mt-1">Select a destination project under the same client</p>
              </div>
              
              <div className="p-6">
                {availableProjects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FolderInput className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No other projects available</p>
                    <p className="text-sm mt-2">No other projects exist under this client.</p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Destination Project
                    </label>
                    <select
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select a project...</option>
                      {availableProjects.map((proj) => (
                        <option key={proj.id} value={proj.id}>
                          {proj.name}
                        </option>
                      ))}
                    </select>
                    
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> This task will be moved to the first stage of the selected project.
                        {task?.parent_task_id ? '' : ' All subtasks will be moved along with this task.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowMoveProjectModal(false);
                    setSelectedProjectId('');
                  }}
                  disabled={movingTask}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMoveToProject}
                  disabled={!selectedProjectId || movingTask}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {movingTask ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Moving...</span>
                    </>
                  ) : (
                    <>
                      <FolderInput className="w-4 h-4" />
                      <span>Move Task</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-y-auto">
            {/* Task Title */}
            <div className="p-6 border-b border-gray-200">
              <div className="relative flex items-start gap-4">
                <div className="flex-1">
                  <InputWithVariablePicker
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onBlur={() => {
                      // For new tasks, auto-save when name is filled and user leaves the field
                      if (!task?.id && formData.name?.trim()) {
                        triggerAutoSave();
                      }
                    }}
                    className="text-3xl font-bold text-gray-900 border-none outline-none placeholder-gray-400 bg-transparent"
                    placeholder="Task name (e.g., Setup meeting with {{client.name}})"
                    clientId={project?.client_id}
                    showVariablePicker={true}
                    required
                  />
                </div>
                {/* Client Portal Toggle - Side by side with Variable Picker */}
                <div className="flex items-center gap-2 pt-2">
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Client Portal</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, show_in_portal: !formData.show_in_portal })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                      formData.show_in_portal ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.show_in_portal ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Properties Grid - 3x3x3x2 Layout (11 fields) */}
            <div className="p-6 border-b border-gray-200">
              <form onSubmit={handleSubmit} className="relative">
                <button
                  type="button"
                  onClick={() => setShowTaskDetails(prev => !prev)}
                  className="absolute top-0 right-0 p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  title={showTaskDetails ? 'Collapse details' : 'Expand details'}
                  aria-label={showTaskDetails ? 'Collapse details' : 'Expand details'}
                >
                  {showTaskDetails ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>

                <div className={`space-y-6 mb-6 ${showTaskDetails ? '' : 'hidden'}`}>
                  {/* Row 1 - 3 fields */}
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          // Find the stage object that matches this status
                          const matchingStage = stages.find(s => (s.name || s) === newStatus);
                          // Update both status and stage_id to keep them in sync
                          setFormData({ 
                            ...formData, 
                            status: newStatus,
                            stage_id: matchingStage?.id || formData.stage_id
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {stages.length > 0 ? stages.map((stage) => (
                          <option key={stage.id || stage} value={stage.name || stage}>{stage.name || stage}</option>
                        )) : Object.keys(STATUSES).map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {Object.keys(PRIORITIES).map((priority) => (
                          <option key={priority} value={priority}>{priority}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                      <input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Row 2 - 3 fields */}
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Due Time</label>
                      <input
                        type="time"
                        value={formData.due_time}
                        onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Time Requirement</label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={effortValue}
                          onChange={(e) => setEffortValue(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="e.g., 30"
                        />
                        <select
                          value={effortUnit}
                          onChange={(e) => setEffortUnit(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                        >
                          <option value="Minutes">Minutes</option>
                          <option value="Hours">Hours</option>
                          <option value="Days">Days</option>
                        </select>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Specify how long this task will take (1 Day = 8 work hours)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Assignees</label>
                      <div className="flex flex-wrap gap-2">
                        {teamMembers.length > 0 ? (
                          <div className="w-full max-h-20 overflow-y-auto">
                            {teamMembers.map((member) => (
                              <div key={member.user.id} className="flex items-center space-x-2 mb-1">
                                <input
                                  type="checkbox"
                                  id={`assignee-${member.user.id}`}
                                  checked={formData.assignees.includes(member.user.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        assignees: [...formData.assignees, member.user.id],
                                        assignees_updated: true
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        assignees: formData.assignees.filter(id => id !== member.user.id),
                                        assignees_updated: true
                                      });
                                    }
                                  }}
                                  className="w-3 h-3 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                />
                                <label 
                                  htmlFor={`assignee-${member.user.id}`}
                                  className="text-xs cursor-pointer text-gray-700"
                                >
                                  {member.user.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No team members</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Row 3 - 3 fields */}
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                      <input
                        type="text"
                        value={formData.tags ? formData.tags.join(', ') : ''}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="tag1, tag2, tag3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
                      <input
                        type="text"
                        value={project?.name || 'No Project'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        disabled
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select category...</option>
                        {taskCategories.length > 0 ? (
                          taskCategories.map((category) => (
                            <option key={category.id} value={category.name}>
                              {category.name}
                            </option>
                          ))
                        ) : (
                          <>
                            {/* Fallback categories if none are configured */}
                            <option value="Feature">Feature</option>
                            <option value="Bug">Bug</option>
                            <option value="Deliverable">Deliverable</option>
                            <option value="Meeting Prep">Meeting Prep</option>
                            <option value="Research">Research</option>
                            <option value="Documentation">Documentation</option>
                          </>
                        )}
                      </select>
                      {taskCategories.length === 0 && (
                        <p className="text-sm text-gray-500 mt-1">
                          No custom categories configured. You can create custom categories in Settings → Task Categories.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Row 4 - 2 fields (Scheduling & Recurring) */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={formData.start_date || ''}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        title="When should this task begin?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Recurring</label>
                      <select
                        value={formData.recurring_rule ? 'configured' : ''}
                        onChange={(e) => {
                          if (e.target.value === 'configured') {
                            // Allow editing existing recurring rule by opening modal
                            setShowRecurringModal(true);
                            return;
                          }
                          if (e.target.value === '') {
                            setFormData({ ...formData, recurring_rule: '', is_recurring_template: false });
                            setRecurringPreview('');
                          } else {
                            setShowRecurringModal(true);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">No recurrence</option>
                        {formData.recurring_rule && <option value="configured">{recurringPreview || 'Custom recurring'} (click to edit)</option>}
                        <option value="quick-daily">Daily</option>
                        <option value="quick-weekly">Weekly</option>
                        <option value="quick-biweekly">Every 2 weeks</option>
                        <option value="quick-monthly">Monthly</option>
                        <option value="quick-quarterly">Quarterly</option>
                        <option value="quick-yearly">Yearly</option>
                        <option value="custom">Custom...</option>
                      </select>
                      {recurringPreview && (
                        <p className="text-xs text-gray-600 mt-1">{recurringPreview}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    onMentionClick={handleMentionClick}
                    placeholder="Describe the task requirements, context, and acceptance criteria... Use @ to mention team members and variables for dynamic content"
                    enableMentions={true}
                    enableVariables={true}
                    clientId={project?.client_id}
                    contactId={null}
                    taskId={task?.id}
                    onRequestTaskSave={async () => {
                      // If task doesn't have ID, trigger auto-save first
                      if (!task?.id && formData.name?.trim()) {
                        await triggerAutoSave();
                        return { id: task?.id };
                      }
                      return task;
                    }}
                  />
                </div>

                {/* Milestone Toggle */}
                <div className="flex items-center space-x-3 mb-6">
                  <input
                    type="checkbox"
                    id="is_milestone"
                    checked={formData.is_milestone}
                    onChange={(e) => setFormData({ ...formData, is_milestone: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="is_milestone" className="text-sm font-medium text-gray-700">
                    Mark as milestone
                  </label>
                </div>

                {/* Bottom Tabs for Subtasks, Uploads, Dependencies */}
                <div className="mt-8">
                  <div className="border-b border-gray-200">
                    <nav className="flex space-x-8">
                        <button
                          type="button"
                          onClick={() => setActiveSubTab('subtasks')}
                          className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeSubTab === 'subtasks'
                              ? 'border-purple-500 text-purple-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Subtasks ({subtasks.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSubTab('uploads')}
                          className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeSubTab === 'uploads'
                              ? 'border-purple-500 text-purple-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Uploads ({attachments.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSubTab('workspace')}
                          className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeSubTab === 'workspace'
                              ? 'border-purple-500 text-purple-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Workspace
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSubTab('deliverables')}
                          className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeSubTab === 'deliverables'
                              ? 'border-purple-500 text-purple-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Deliverables ({deliverables.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSubTab('dependencies')}
                          className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeSubTab === 'dependencies'
                              ? 'border-purple-500 text-purple-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Dependencies {dependencies.is_blocked && <span className="ml-1 w-2 h-2 rounded-full bg-red-500 inline-block" />}
                          {` (${(dependencies.blocking?.length || 0) + (dependencies.blocked_by?.length || 0)})`}
                        </button>
                      </nav>
                    </div>

                    {/* Tab Content */}
                    <div className="py-4">
                      {activeSubTab === 'subtasks' && (
                        <div className="space-y-4">
                          {/* Info message for unsaved tasks */}
                          {!task?.id && (
                            <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                              <div className="text-sm text-blue-800">
                                <p className="font-medium">Auto-save enabled</p>
                                <p className="text-xs mt-1">When you create a subtask, the parent task will be saved automatically. Make sure to fill in the task name first.</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Subtask Progress Summary */}
                          {subtasks.length > 0 && task?.id && (() => {
                            // Check completion statuses
                            const completionStatuses = ['Complete', 'Done', 'Completed'];
                            const completedCount = subtasks.filter(s => completionStatuses.includes(s.status)).length;
                            const percentage = (completedCount / subtasks.length) * 100;
                            
                            return (
                              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <span className="text-sm font-medium text-purple-900">
                                  {completedCount} of {subtasks.length} completed
                                </span>
                                <div className="w-48 bg-purple-200 rounded-full h-2">
                                  <div 
                                    className="bg-purple-600 h-2 rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })()}

                          {/* Subtask List - ClickUp Style */}
                          <div className="space-y-1">
                            {subtasks.length > 0 ? (
                              subtasks.map((subtask) => {
                                // Check completion - subtasks use 'status' field
                                // Common completion statuses: 'Complete', 'Done', 'Completed'
                                const completionStatuses = ['Complete', 'Done', 'Completed'];
                                const isCompleted = completionStatuses.includes(subtask.status);
                                
                                return (
                                <div 
                                  key={subtask.id} 
                                  className="group flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                                >
                                  {/* Custom Purple Checkbox */}
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      
                                      if (!task?.id) {
                                        alert('Please save the parent task first');
                                        return;
                                      }
                                      
                                      try {
                                        await handleSubtaskStatusToggle(subtask);
                                        
                                      } catch (error) {
                                        console.error('❌ Failed to update:', error);
                                        // Revert on error
                                        loadTaskSubtasks(task.id);
                                        alert('Failed to update subtask status');
                                      }
                                    }}
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
                                      isCompleted 
                                        ? 'bg-purple-600 border-purple-600' 
                                        : 'border-gray-300 hover:border-purple-400 bg-white'
                                    }`}
                                  >
                                    {isCompleted && (
                                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </button>
                                  <div 
                                    className="flex-1 min-w-0 cursor-pointer"
                                    onClick={async () => {
                                      // Auto-save parent task before opening subtask to preserve changes
                                      if (task?.id) {
                                        try {
                                          const submitData = { ...formData, _autoSave: true }; // Silent save
                                          await saveTask(submitData);
                                        } catch (error) {
                                          console.error('Failed to save parent task:', error);
                                        }
                                      }
                                      
                                      // Directly open subtask without closing modal (seamless transition)
                                      if (onEditSubtask) {
                                        onEditSubtask(subtask);
                                      }
                                    }}
                                    title="Click to edit subtask"
                                  >
                                    <div className="flex items-center space-x-2">
                                      <span className={`text-sm transition-all ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                                        {subtask.name}
                                      </span>
                                      {subtask.stage_name && !isCompleted && (
                                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                          {subtask.stage_name}
                                        </span>
                                      )}
                                      {isCompleted && (
                                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded">
                                          Complete
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Quick Action Icons - Always Visible on Hover */}
                                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {/* Assignee Quick Edit */}
                                    <div className="relative">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuickEditSubtaskId(subtask.id);
                                          setQuickEditField('assignees');
                                        }}
                                        className={`p-1 rounded transition-colors ${
                                          subtask.assignees?.length > 0
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                                        }`}
                                        title={subtask.assignees?.length > 0 ? `Assigned to: ${getAssigneeNames(subtask.assignees)}` : 'Assign users'}
                                      >
                                        <User className="w-3 h-3" />
                                        {subtask.assignees?.length > 0 && (
                                          <span className="text-xs ml-0.5">{subtask.assignees.length}</span>
                                        )}
                                      </button>
                                      {quickEditSubtaskId === subtask.id && quickEditField === 'assignees' && (
                                        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-3 z-50 w-72">
                                          <div className="text-sm font-semibold text-gray-800 mb-2">Assign to:</div>
                                          
                                          {/* Search Input */}
                                          <input
                                            type="text"
                                            placeholder="Search team members..."
                                            value={quickEditField === 'assignees' ? (window.memberSearchQuery || '') : ''}
                                            onChange={(e) => {
                                              window.memberSearchQuery = e.target.value;
                                              setQuickEditSubtaskId(subtask.id); // Force re-render
                                            }}
                                            className="w-full px-3 py-2 mb-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            autoFocus
                                          />
                                          
                                          <div className="space-y-1 max-h-48 overflow-y-auto">
                                            {!teamMembers || teamMembers.length === 0 ? (
                                              <div className="text-sm text-gray-500 p-2">No team members available</div>
                                            ) : (
                                              (() => {
                                                const searchQuery = (window.memberSearchQuery || '').toLowerCase();
                                                const filteredMembers = teamMembers.filter(member => {
                                                  // Get display name - teamMembers has structure {user: {id, name, email}}
                                                  const displayName = member.user?.name || member.user?.email || member.name || member.email || 'Unknown';
                                                  return displayName.toLowerCase().includes(searchQuery);
                                                });
                                                
                                                return filteredMembers.length === 0 ? (
                                                  <div className="text-sm text-gray-500 p-2">No matches found</div>
                                                ) : (
                                                  filteredMembers.map(member => {
                                                    // Get member ID and display name - structure is {user: {id, name, email}}
                                                    const memberId = member.user?.id || member.id;
                                                    const displayName = member.user?.name || member.user?.email || member.name || member.email || 'Unknown';
                                                    
                                                    return (
                                                      <label key={memberId} className="flex items-center space-x-3 cursor-pointer hover:bg-purple-50 p-2 rounded transition-colors">
                                                        <input
                                                          type="checkbox"
                                                          checked={subtask.assignees?.includes(memberId) || false}
                                                          onChange={(e) => {
                                                            const currentAssignees = subtask.assignees || [];
                                                            const newAssignees = e.target.checked
                                                              ? [...currentAssignees, memberId]
                                                              : currentAssignees.filter(id => id !== memberId);
                                                            handleQuickUpdateSubtask(subtask.id, 'assignees', newAssignees);
                                                          }}
                                                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                                        />
                                                        <span className="text-base font-medium text-gray-900">{displayName}</span>
                                                      </label>
                                                    );
                                                  })
                                                );
                                              })()
                                            )}
                                          </div>
                                          <button
                                            onClick={() => {
                                              window.memberSearchQuery = '';
                                              setQuickEditSubtaskId(null);
                                              setQuickEditField(null);
                                            }}
                                            className="mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium w-full text-center py-2 hover:bg-purple-50 rounded border border-purple-200"
                                          >
                                            Close
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* Priority Quick Edit */}
                                    <div className="relative">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuickEditSubtaskId(subtask.id);
                                          setQuickEditField('priority');
                                        }}
                                        className={`p-1 rounded transition-colors ${
                                          subtask.priority && subtask.priority !== 'Normal'
                                            ? subtask.priority === 'Urgent' ? 'bg-red-100 text-red-700'
                                            : subtask.priority === 'High' ? 'bg-orange-100 text-orange-700'
                                            : 'bg-blue-100 text-blue-700'
                                            : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'
                                        }`}
                                        title="Set priority"
                                      >
                                        <Flag className="w-3 h-3" />
                                      </button>
                                      {quickEditSubtaskId === subtask.id && quickEditField === 'priority' && (
                                        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-2 z-50 w-36">
                                          <div className="text-sm font-semibold text-gray-800 mb-2">Priority:</div>
                                          {['Low', 'Normal', 'High', 'Urgent'].map(priority => (
                                            <button
                                              key={priority}
                                              onClick={() => handleQuickUpdateSubtask(subtask.id, 'priority', priority)}
                                              className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-purple-50 transition-colors ${
                                                subtask.priority === priority ? 'bg-purple-100 text-purple-700 font-semibold' : 'text-gray-800'
                                              }`}
                                            >
                                              {priority}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    {/* Stage/Status Quick Edit */}
                                    <div className="relative">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuickEditSubtaskId(subtask.id);
                                          setQuickEditField('stage');
                                        }}
                                        className={`p-1 rounded transition-colors ${
                                          subtask.stage_name
                                            ? 'bg-indigo-100 text-indigo-700'
                                            : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
                                        }`}
                                        title={subtask.stage_name ? `Stage: ${subtask.stage_name}` : 'Set stage'}
                                      >
                                        <TargetIcon className="w-3 h-3" />
                                      </button>
                                      {quickEditSubtaskId === subtask.id && quickEditField === 'stage' && (
                                        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-3 z-50 w-64">
                                          <div className="text-base font-bold mb-3" style={{color: '#111827'}}>Move to Stage:</div>
                                          <div className="space-y-1 max-h-64 overflow-y-auto">
                                            {/* Available stages dropdown */}
                                            {!stages || stages.length === 0 ? (
                                              <div className="text-sm text-red-600 p-2">No stages available</div>
                                            ) : (
                                              stages.map((stage, index) => {
                                                // Stages can be STRINGS or OBJECTS
                                                const stageName = stage.name || stage;
                                                // Stage name processing
                                                return (
                                                  <button
                                                    key={stage.id || index}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      // Change stage
                                                      handleQuickUpdateSubtask(subtask.id, 'status', stageName);
                                                    }}
                                                    className={`w-full text-left px-4 py-3 rounded hover:bg-purple-50 transition-colors ${
                                                      subtask.status === stageName ? 'bg-purple-100' : 'bg-white'
                                                    }`}
                                                    style={{
                                                      color: '#111827',
                                                      fontSize: '16px',
                                                      fontWeight: subtask.status === stageName ? '700' : '500'
                                                    }}
                                                  >
                                                    <div className="flex items-center justify-between">
                                                      <span style={{color: '#111827'}}>{stageName}</span>
                                                      {subtask.status === stageName && (
                                                        <CheckCircle className="w-5 h-5 text-purple-600" />
                                                      )}
                                                    </div>
                                                  </button>
                                                );
                                              })
                                            )}
                                          </div>
                                          <button
                                            onClick={() => {
                                              setQuickEditSubtaskId(null);
                                              setQuickEditField(null);
                                            }}
                                            className="mt-3 w-full text-center py-2 rounded border-2"
                                            style={{
                                              color: '#7C3AED',
                                              borderColor: '#7C3AED',
                                              fontWeight: '600'
                                            }}
                                          >
                                            Close
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* Due Date Quick Edit */}
                                    <div className="relative">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setQuickEditSubtaskId(subtask.id);
                                          setQuickEditField('due_date');
                                        }}
                                        className={`p-1 rounded transition-colors ${
                                          subtask.due_date
                                            ? 'bg-green-100 text-green-700'
                                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                                        }`}
                                        title="Set due date"
                                      >
                                        <Calendar className="w-3 h-3" />
                                      </button>
                                      {quickEditSubtaskId === subtask.id && quickEditField === 'due_date' && (
                                        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-3 z-50">
                                          <div className="text-sm font-semibold text-gray-800 mb-2">Due Date:</div>
                                          <input
                                            type="date"
                                            value={subtask.due_date ? new Date(subtask.due_date).toISOString().split('T')[0] : ''}
                                            onChange={(e) => handleQuickUpdateSubtask(subtask.id, 'due_date', e.target.value)}
                                            className="text-sm border border-gray-300 rounded px-3 py-2 w-full focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                            autoFocus
                                          />
                                          <button
                                            onClick={() => {
                                              setQuickEditSubtaskId(null);
                                              setQuickEditField(null);
                                            }}
                                            className="mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium w-full text-center py-1 hover:bg-purple-50 rounded"
                                          >
                                            Done
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* Delete Button */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteSubtask(subtask.id);
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Delete subtask"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              );
                              })
                            ) : (
                              <div className="text-center py-6 text-gray-400">
                                <p className="text-sm">No subtasks yet</p>
                              </div>
                            )}
                          </div>

                          {/* Add Subtask - Single Line Input (ClickUp Style) */}
                          <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 transition-colors">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                              disabled
                            />
                            <input
                              type="text"
                              placeholder="+ Add subtask"
                              value={newSubtaskData.name}
                              onChange={(e) => setNewSubtaskData({ ...newSubtaskData, name: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newSubtaskData.name.trim()) {
                                  e.preventDefault();
                                  handleCreateSubtask();
                                } else if (e.key === 'Escape') {
                                  setNewSubtaskData({ name: '', description: '', priority: 'Normal', status: 'To Do', assignees: [], due_date: '', estimated_effort_value: '', estimated_effort_unit: 'hours' });
                                }
                              }}
                              className="flex-1 bg-transparent border-none focus:outline-none text-sm placeholder-gray-400"
                            />
                            {/* Quick Action Icons */}
                            <div className="flex items-center space-x-1">
                              <button
                                type="button"
                                onClick={() => {
                                  // Quick assign - could open a dropdown
                                  alert('Quick assign feature - click subtask to edit');
                                }}
                                className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                title="Assign"
                              >
                                <User className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  // Quick priority - could open a dropdown
                                  alert('Quick priority - click subtask to edit');
                                }}
                                className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                title="Priority"
                              >
                                <Flag className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  // Quick due date - could open a date picker
                                  alert('Quick due date - click subtask to edit');
                                }}
                                className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                title="Due Date"
                              >
                                <Calendar className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeSubTab === 'workspace' && (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600">Task workspace for working notes, links, screenshots, and context.</p>
                          <RichTextEditor
                            value={formData.workspace_content || ''}
                            onChange={handleWorkspaceChange}
                            placeholder="Add working notes, links, and pasted images for this task..."
                            enableMentions={false}
                            enableVariables={false}
                            clientId={project?.client_id}
                            contactId={null}
                            taskId={task?.id}
                            onRequestTaskSave={async () => {
                              if (!task?.id && formData.name?.trim()) {
                                await triggerAutoSave();
                                return { id: task?.id };
                              }
                              return task;
                            }}
                          />
                          <div className="text-xs text-gray-500">Images pasted here are uploaded as task attachments and embedded in the workspace.</div>
                          {formData.workspace_updated_at && (
                            <div className="text-xs text-gray-400">
                              Last updated {new Date(formData.workspace_updated_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}

                      {activeSubTab === 'uploads' && (
                        <div className="space-y-4">
                          {/* Uploaded Files List */}
                          <div className="space-y-2">
                            {attachments.map((attachment, index) => (
                              <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (openAttachmentPreview) {
                                      openAttachmentPreview({
                                        id: attachment.id,
                                        task_id: attachment.task_id || task?.id,
                                        name: attachment.filename,
                                        filename: attachment.filename,
                                        url: attachment.file_url,
                                        file_url: attachment.file_url,
                                        size: attachment.file_size,
                                        file_size: attachment.file_size,
                                        type: attachment.content_type,
                                        content_type: attachment.content_type
                                      });
                                    }
                                  }}
                                  className="flex items-center space-x-3 flex-1 text-left hover:bg-purple-50 rounded p-2 transition-colors"
                                >
                                  <Paperclip className="w-4 h-4 text-gray-400" />
                                  <div>
                                    <div className="font-medium text-gray-900 text-sm">{attachment.filename}</div>
                                    <div className="text-xs text-gray-500">
                                      {(attachment.file_size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                  </div>
                                  <Eye className="w-4 h-4 text-purple-500 ml-auto" />
                                </button>
                                <div className="flex items-center space-x-2 ml-2">
                                  <button
                                    onClick={() => handleDeleteAttachment(index)}
                                    className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* File Upload Area */}
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                            <p className="text-sm text-gray-600 mb-2">Drop files or click to upload</p>
                            <input
                              type="file"
                              multiple
                              onChange={(e) => Array.from(e.target.files).forEach(file => handleFileUpload(file))}
                              className="hidden"
                              id="file-upload"
                            />
                            <label
                              htmlFor="file-upload"
                              className="cursor-pointer px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                            >
                              Choose Files
                            </label>
                          </div>
                        </div>
                      )}

                      {activeSubTab === 'deliverables' && (
                        <div className="space-y-6">
                          {/* Review Notes Section */}
                          <div className="space-y-3">
                            <label className="block text-sm font-medium text-gray-700">
                              Review Notes
                            </label>
                            <textarea
                              value={formData.review_notes}
                              onChange={(e) => setFormData({ ...formData, review_notes: e.target.value })}
                              placeholder="Add notes about the task review, feedback, or deliverable instructions..."
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                            />
                          </div>

                          {/* Deliverables List */}
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-medium text-gray-700">Work Deliverables</h4>
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => setShowDeliverableForm(!showDeliverableForm)}
                                  className="flex items-center space-x-1 px-3 py-1 text-purple-600 hover:text-purple-700 text-sm border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
                                >
                                  <Link className="w-4 h-4" />
                                  <span>Add Link</span>
                                </button>
                                <label className="flex items-center space-x-1 px-3 py-1 text-purple-600 hover:text-purple-700 text-sm border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer">
                                  <Upload className="w-4 h-4" />
                                  <span>{uploadingDeliverable ? 'Uploading...' : 'Upload File'}</span>
                                  <input
                                    type="file"
                                    multiple
                                    onChange={(e) => Array.from(e.target.files).forEach(file => {
                                      const title = prompt(`Enter a title for ${file.name}:`, file.name);
                                      if (title !== null) {
                                        handleUploadDeliverableFile(file, title);
                                      }
                                    })}
                                    className="hidden"
                                    disabled={uploadingDeliverable}
                                  />
                                </label>
                              </div>
                            </div>

                            {/* Add Link Form */}
                            {showDeliverableForm && (
                              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Title (Optional)
                                  </label>
                                  <input
                                    type="text"
                                    value={newDeliverableTitle}
                                    onChange={(e) => setNewDeliverableTitle(e.target.value)}
                                    placeholder="e.g., Logo Design v2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Link URL *
                                  </label>
                                  <input
                                    type="url"
                                    value={newDeliverableLink}
                                    onChange={(e) => setNewDeliverableLink(e.target.value)}
                                    placeholder="https://example.com/deliverable"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                  />
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowDeliverableForm(false);
                                      setNewDeliverableLink('');
                                      setNewDeliverableTitle('');
                                    }}
                                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleAddDeliverableLink}
                                    disabled={!newDeliverableLink.trim()}
                                    className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Add Link
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Deliverables List */}
                            <div className="space-y-2">
                              {deliverables.length > 0 ? (
                                deliverables.map((deliverable) => (
                                  <div key={deliverable.id} className="p-3 bg-gray-50 rounded-lg border">
                                    <div className="flex items-start justify-between">
                                      <div className="flex items-start space-x-3 flex-1">
                                        {deliverable.deliverable_type === 'link' ? (
                                          <Link className="w-4 h-4 text-blue-500 mt-0.5" />
                                        ) : (
                                          <FileText className="w-4 h-4 text-green-500 mt-0.5" />
                                        )}
                                        <div className="flex-1">
                                          <div className="font-medium text-gray-900 text-sm">
                                            {deliverable.title || deliverable.file_name || 'Untitled'}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {deliverable.deliverable_type === 'file' && deliverable.file_size && (
                                              <span>{(deliverable.file_size / 1024 / 1024).toFixed(2)} MB • </span>
                                            )}
                                            {deliverable.deliverable_type === 'file' ? 'File Upload' : 'External Link'}
                                            {deliverable.status && (
                                              <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                                                deliverable.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                deliverable.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                                'bg-yellow-100 text-yellow-700'
                                              }`}>
                                                {deliverable.status}
                                              </span>
                                            )}
                                          </div>
                                          {deliverable.deliverable_type === 'link' && deliverable.url && (
                                            <a 
                                              href={deliverable.url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline break-all mt-1 block"
                                            >
                                              {deliverable.url}
                                            </a>
                                          )}
                                          {deliverable.description && (
                                            <div className="text-xs text-gray-600 mt-1">
                                              {deliverable.description}
                                            </div>
                                          )}
                                          {deliverable.review_notes && (
                                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                              <div className="font-medium text-blue-900 mb-1">Review Notes:</div>
                                              <div className="text-blue-800 whitespace-pre-wrap">{deliverable.review_notes}</div>
                                              {deliverable.reviewed_at && (
                                                <div className="text-blue-600 mt-1 text-[10px]">
                                                  Reviewed {new Date(deliverable.reviewed_at).toLocaleDateString()}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-2 ml-2">
                                        {deliverable.url && (
                                          deliverable.deliverable_type === 'link' ? (
                                            <a
                                              href={deliverable.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              className="flex items-center space-x-1 px-2 py-1 text-blue-600 hover:text-blue-700 text-xs border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                              <span>Open Link</span>
                                            </a>
                                          ) : (
                                            <button
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                openAttachmentPreview && openAttachmentPreview({
                                                  name: deliverable.title || deliverable.file_name || 'Deliverable',
                                                  filename: deliverable.title || deliverable.file_name || 'Deliverable',
                                                  url: deliverable.url,
                                                  file_url: deliverable.url,
                                                  size: deliverable.file_size,
                                                  file_size: deliverable.file_size,
                                                  type: deliverable.content_type
                                                });
                                              }}
                                              className="flex items-center space-x-1 px-2 py-1 text-purple-600 hover:text-purple-700 text-xs border border-purple-200 rounded hover:bg-purple-50 transition-colors"
                                            >
                                              <Eye className="w-3 h-3" />
                                              <span>Preview</span>
                                            </button>
                                          )
                                        )}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setViewingDeliverable(deliverable.id);
                                          }}
                                          className="flex items-center space-x-1 px-2 py-1 text-purple-600 hover:text-purple-700 text-xs border border-purple-200 rounded hover:bg-purple-50 transition-colors"
                                        >
                                          <Eye className="w-3 h-3" />
                                          <span>View</span>
                                        </button>
                                        {deliverable.deliverable_type === 'file' && (
                                          <button
                                            type="button"
                                            onClick={() => handleReplaceDeliverableFile(deliverable.id)}
                                            className="flex items-center space-x-1 px-2 py-1 text-blue-600 hover:text-blue-700 text-xs border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                                          >
                                            <RefreshCw className="w-3 h-3" />
                                            <span>Replace</span>
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteDeliverable(deliverable.id)}
                                          className="flex items-center space-x-1 px-2 py-1 text-red-600 hover:text-red-700 text-xs border border-red-200 rounded hover:bg-red-50 transition-colors"
                                        >
                                          <Trash className="w-3 h-3" />
                                          <span>Delete</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="text-center py-8 text-gray-500">
                                  <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                  <p className="text-sm">No deliverables yet</p>
                                  <p className="text-xs">Add links or upload files to share work outputs with clients</p>
                                </div>
                              )}
                            </div>

                            {/* Usage Instructions */}
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <div className="flex items-start space-x-2">
                                <Eye className="w-4 h-4 text-yellow-600 mt-0.5" />
                                <div className="text-sm">
                                  <p className="font-medium text-yellow-800">Email Template Variables</p>
                                  <div className="text-yellow-700 text-xs mt-1 space-y-1">
                                    <p><code className="bg-yellow-100 px-1 rounded">{"{{task.review_notes}}"}</code> - Execution team feedback and comments</p>
                                    <p><code className="bg-yellow-100 px-1 rounded">{"{{task.deliverables}}"}</code> - List of deliverable files and links</p>
                                    <p className="mt-2 text-yellow-600">Use separately for flexible email formatting or together for complete task summaries.</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Edit Deliverable Modal */}
                            {editingDeliverable && (
                              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                                  <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Edit Deliverable</h3>
                                    <button
                                      onClick={() => setEditingDeliverable(null)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      <X className="w-5 h-5" />
                                    </button>
                                  </div>

                                  <div className="space-y-4">
                                    {/* Title */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Title
                                      </label>
                                      <input
                                        type="text"
                                        value={editDeliverableData.title}
                                        onChange={(e) => setEditDeliverableData({ ...editDeliverableData, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="Deliverable title"
                                      />
                                    </div>

                                    {/* Description */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description
                                      </label>
                                      <textarea
                                        value={editDeliverableData.description}
                                        onChange={(e) => setEditDeliverableData({ ...editDeliverableData, description: e.target.value })}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="Brief description of this deliverable"
                                      />
                                    </div>

                                    {/* Status */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                      </label>
                                      <select
                                        value={editDeliverableData.status}
                                        onChange={(e) => setEditDeliverableData({ ...editDeliverableData, status: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                      >
                                        <option value="pending">Pending Review</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Needs Revision</option>
                                      </select>
                                    </div>

                                    {/* Review Notes */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Review Notes
                                      </label>
                                      <textarea
                                        value={editDeliverableData.review_notes}
                                        onChange={(e) => setEditDeliverableData({ ...editDeliverableData, review_notes: e.target.value })}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="Add your review feedback, approval notes, or revision requests..."
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        These notes will be saved with your name and timestamp
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex space-x-3 mt-6">
                                    <button
                                      onClick={() => setEditingDeliverable(null)}
                                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={handleUpdateDeliverable}
                                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                    >
                                      Save Changes
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Deliverable View Modal moved to end of component */}

                          </div>
                        </div>
                      )}

                      {activeSubTab === 'dependencies' && (
                        <div className="space-y-6">
                          {/* Info message for unsaved tasks */}
                          {!task?.id && (
                            <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                              <div className="text-sm text-blue-800">
                                <p className="font-medium">Save the task first</p>
                                <p className="text-blue-600">Dependencies can be added after the task is saved.</p>
                              </div>
                            </div>
                          )}

                          {task?.id && (
                            <>
                              {/* Blocked Status Warning */}
                              {dependencies.is_blocked && (
                                <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="font-medium text-amber-800">This task is blocked</p>
                                    <p className="text-sm text-amber-700 mt-1">
                                      {dependencies.unresolved_blocker_count} blocking task(s) must be completed before this task can be scheduled.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Add Dependency Section */}
                              <div className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-medium text-gray-900">Add Dependency</h4>
                                  <select
                                    value={dependencyType}
                                    onChange={(e) => setDependencyType(e.target.value)}
                                    className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  >
                                    <option value="blocked_by">This task is blocked by...</option>
                                    <option value="blocks">This task blocks...</option>
                                  </select>
                                </div>
                                
                                <div className="relative">
                                  <input
                                    type="text"
                                    placeholder="Search for a task to link..."
                                    value={dependencySearchTerm}
                                    onChange={(e) => setDependencySearchTerm(e.target.value)}
                                    onFocus={() => setShowAddDependencyDropdown(true)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  />
                                  
                                  {/* Search Results Dropdown */}
                                  {showAddDependencyDropdown && (dependencySearchResults.length > 0 || searchingDependencies) && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                      {searchingDependencies ? (
                                        <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
                                      ) : (
                                        dependencySearchResults.map((searchTask) => (
                                          <button
                                            key={searchTask.id}
                                            type="button"
                                            onClick={() => handleAddDependency(searchTask.id, dependencyType)}
                                            disabled={addingDependency}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                                          >
                                            <div className="font-medium text-gray-900 text-sm">{searchTask.title}</div>
                                            <div className="flex items-center gap-2 mt-1">
                                              {searchTask.project_name && (
                                                <span className="text-xs text-gray-500">📁 {searchTask.project_name}</span>
                                              )}
                                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                searchTask.status === 'Done' || searchTask.status === 'Complete' 
                                                  ? 'bg-green-100 text-green-700' 
                                                  : 'bg-blue-100 text-blue-700'
                                              }`}>
                                                {searchTask.status}
                                              </span>
                                            </div>
                                          </button>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </div>
                                
                                {dependencySearchTerm.length > 0 && dependencySearchTerm.length < 2 && (
                                  <p className="text-xs text-gray-500 mt-2">Type at least 2 characters to search</p>
                                )}
                              </div>

                              {/* Blocked By Section */}
                              <div>
                                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                  Blocked By ({dependencies.blocked_by?.length || 0})
                                </h4>
                                {dependencies.blocked_by?.length > 0 ? (
                                  <div className="space-y-2">
                                    {dependencies.blocked_by.map((dep) => (
                                      <div 
                                        key={dep.dependency_id} 
                                        className={`flex items-center justify-between p-3 rounded-lg border ${
                                          dep.is_resolved 
                                            ? 'bg-green-50 border-green-200' 
                                            : 'bg-red-50 border-red-200'
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          {dep.is_resolved ? (
                                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                                          ) : (
                                            <XCircle className="w-5 h-5 text-red-500" />
                                          )}
                                          <div>
                                            <p className={`font-medium text-sm ${dep.is_resolved ? 'text-green-800 line-through' : 'text-red-800'}`}>
                                              {dep.title}
                                            </p>
                                            {dep.project_name && (
                                              <p className="text-xs text-gray-500 mt-0.5">📁 {dep.project_name}</p>
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveDependency(dep.dependency_id)}
                                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded"
                                          title="Remove dependency"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 italic">No blocking dependencies</p>
                                )}
                              </div>

                              {/* Blocking Section */}
                              <div>
                                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                  Blocking ({dependencies.blocking?.length || 0})
                                </h4>
                                {dependencies.blocking?.length > 0 ? (
                                  <div className="space-y-2">
                                    {dependencies.blocking.map((dep) => (
                                      <div 
                                        key={dep.dependency_id} 
                                        className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg"
                                      >
                                        <div className="flex items-center gap-3">
                                          <AlertTriangle className="w-5 h-5 text-orange-500" />
                                          <div>
                                            <p className="font-medium text-sm text-orange-800">{dep.title}</p>
                                            {dep.project_name && (
                                              <p className="text-xs text-gray-500 mt-0.5">📁 {dep.project_name}</p>
                                            )}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveDependency(dep.dependency_id)}
                                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded"
                                          title="Remove dependency"
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 italic">Not blocking any tasks</p>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {task ? 'Update Task' : 'Create Task'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Right Sidebar - Activity Feed */}
          <div className={`${activityVisible ? 'w-80' : 'w-12'} bg-gray-50 border-l border-gray-200 transition-all duration-300 flex flex-col`}>
            {/* Activity Header */}
            <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
              <h3 className={`font-medium text-gray-900 ${!activityVisible && 'hidden'}`}>Activity</h3>
              <button
                onClick={() => setActivityVisible(!activityVisible)}
                className="p-1 hover:bg-gray-100 rounded text-gray-500"
              >
                {activityVisible ? (
                  <ChevronRight className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4 transform rotate-90" />
                )}
              </button>
            </div>

            {activityVisible && (
              <>
                {/* Activity Content */}
                <div ref={commentsContainerRef} className="flex-1 overflow-y-auto p-4">
                  {task?.id ? (
                    <div className="space-y-4">
                      {comments.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p>Activity will appear here once the task is created</p>
                        </div>
                      ) : (
                        organizeComments(comments).map((comment) => (
                          <CommentItem 
                            key={comment.id} 
                            comment={comment} 
                            depth={0}
                            replyingTo={replyingTo}
                            replyText={replyText}
                            setReplyText={setReplyText}
                            handleStartReply={handleStartReply}
                            handleCancelReply={handleCancelReply}
                            handleAddComment={handleAddComment}
                            handleRetryComment={handleRetryComment}
                            user={user}
                            openAttachmentPreview={openAttachmentPreview}
                            isCommentSending={isCommentSending}
                          />
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>Activity will appear here once the task is created</p>
                    </div>
                  )}
                </div>

                {/* Comment Input */}
                <div className="p-4 pb-16 border-t border-gray-200 bg-white">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                      {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                    </div>
                    <div className="flex-1 relative">
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                        rows="3"
                        placeholder="Add a comment... (@mention teammates, press Enter to send, Shift+Enter for new line)"
                        value={newComment}
                        onChange={handleCommentTextChange}
                        onKeyDown={handleCommentKeyDown}
                      />
                      
                      {/* Mention suggestions dropdown */}
                      {showMentionSuggestions && teamMembers.length > 0 && (
                        <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {teamMembers
                            .filter(member => 
                              !mentionSearchTerm || 
                              member.user.name.toLowerCase().includes(mentionSearchTerm.toLowerCase())
                            )
                            .map(member => (
                              <button
                                key={member.user.id}
                                onClick={() => insertMention(member.user.name)}
                                className="w-full px-3 py-2 text-left hover:bg-purple-50 flex items-center space-x-2 text-sm"
                              >
                                <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-medium overflow-hidden">
                                  {member.user.avatar_url && member.user.avatar_url.trim() !== '' && !member.user.avatar_url.includes('example.com') ? (
                                    <img 
                                      src={member.user.avatar_url} 
                                      alt={member.user.name}
                                      className="w-6 h-6 object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = member.user.name?.split(' ').map(n => n[0]).join('') || 'U';
                                      }}
                                    />
                                  ) : (
                                    member.user.name?.split(' ').map(n => n[0]).join('') || 'U'
                                  )}
                                </div>
                                <span className="font-medium text-gray-900">{member.user.name}</span>
                              </button>
                            ))
                          }
                        </div>
                      )}
                      
                      {/* Comment attachments preview */}
                      {commentAttachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {commentAttachments.map((file, idx) => (
                            <div key={idx} className="flex items-center space-x-2 text-xs bg-gray-50 px-2 py-1 rounded">
                              <Paperclip className="w-3 h-3 text-gray-500" />
                              <span className="flex-1 truncate text-gray-700">{file.name}</span>
                              <button
                                onClick={() => removeCommentAttachment(idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-2 pb-20">
                        <div className="flex items-center space-x-2">
                          <input
                            type="file"
                            id="comment-file-upload"
                            multiple
                            onChange={handleCommentFileUpload}
                            className="hidden"
                          />
                          <label
                            htmlFor="comment-file-upload"
                            className={`cursor-pointer text-gray-400 hover:text-gray-600 ${uploadingCommentFile ? 'opacity-50' : ''}`}
                          >
                            <Paperclip className="w-4 h-4" />
                          </label>
                          <span className="text-xs text-gray-500">
                            Press Enter to send
                          </span>
                        </div>
                        <button 
                          onClick={() => handleAddComment()}
                          disabled={!newComment.trim() || uploadingCommentFile || isCommentSending}
                          className="px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          <Send className="w-4 h-4" />
                          <span>{isCommentSending ? 'Sending...' : 'Send'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recurring Task Configuration Modal */}
      <RecurringTaskModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        onSave={(rule, preview) => {
          // Parse the rule to extract times and settings for backend
          try {
            const parsed = JSON.parse(rule);
            setFormData(prev => ({ 
              ...prev, 
              recurring_rule: rule,
              recurring_due_time: parsed.startTime || '',
              recurring_start_time: parsed.endTime || '', // Use endTime as start_time for time window
              is_recurring_template: parsed.isRecurringTemplate || false
            }));
            // Explicitly mark as having unsaved changes to trigger auto-save
            setHasUnsavedChanges(true);
          } catch {
            // Legacy format
            setFormData(prev => ({ ...prev, recurring_rule: rule }));
            setHasUnsavedChanges(true);
          }
          setRecurringPreview(preview);
        }}
        initialRule={formData.recurring_rule}
      />

      {/* Subtask Completion Validation Modal */}
      {pendingTaskSave && (
        <SubtaskCompletionModal
          isOpen={showSubtaskModal}
          onClose={() => setShowSubtaskModal(false)}
          task={pendingTaskSave.task}
          incompleteSubtasks={pendingTaskSave.incompleteSubtasks}
          newStatus={pendingTaskSave.newStatus}
          previousStatus={pendingTaskSave.previousStatus}
          onConfirmComplete={handleSubtaskModalConfirm}
          onCancel={handleSubtaskModalCancel}
        />
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        userId={selectedUser?.id}
        userName={selectedUser?.name}
      />

      {/* Deliverable View Modal (internal to TaskModal for portability across App and Portal) */}
      {viewingDeliverable && (
        <DeliverableViewModal
          deliverableId={viewingDeliverable}
          onClose={() => setViewingDeliverable(null)}
          currentUser={user}
          onUpdate={() => {
            // Refresh deliverables list after update
            if (task?.id) {
              loadTaskDeliverables(task.id);
            }
          }}
        />
      )}
    </div>
  );
};

const TaskCard = ({ task, onEdit, onDelete, onArchive, teamMembers = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [showSubtasks, setShowSubtasks] = useState(false);

  const PriorityIcon = PRIORITIES[task.priority]?.icon || Circle;
  const StatusIcon = STATUSES[task.status]?.icon || Circle;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  const getAssigneeNames = (assigneeIds) => {
    if (!assigneeIds || assigneeIds.length === 0) return 'Unassigned';
    
    const names = assigneeIds.map(id => {
      const member = teamMembers.find(m => m.user.id === id);
      return member ? member.user.name : 'Unknown User';
    });
    
    return names.join(', ');
  };

  return (
    <div className={`bg-white border rounded-lg p-4 hover:border-gray-300 transition-colors ${
      task.archived ? 'border-gray-300 opacity-75' : 'border-gray-200'
    }`}>
      {/* Task Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <StatusIcon className={`w-5 h-5 ${STATUSES[task.status]?.color}`} />
            <h3 
              className="font-semibold text-gray-900 cursor-pointer hover:text-purple-600"
              onClick={() => setExpanded(!expanded)}
            >
              {task.name}
              {task.is_milestone && <Target className="inline w-4 h-4 ml-2 text-yellow-500" />}
            </h3>
            <PriorityIcon className={`w-4 h-4 ${PRIORITIES[task.priority]?.color}`} />
          </div>

          {/* Task Meta */}
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className={`px-2 py-1 rounded-full text-xs ${PRIORITIES[task.priority]?.bgColor} ${PRIORITIES[task.priority]?.color}`}>
              {task.priority}
            </span>
            
            <span className={`px-2 py-1 rounded-full text-xs ${STATUSES[task.status]?.bgColor} ${STATUSES[task.status]?.color}`}>
              {task.status}
            </span>
            
            {task.archived && (
              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                Archived
              </span>
            )}
            
            {task.category && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                {task.category}
              </span>
            )}
            
            {task.due_date && (
              <span className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(task.due_date)}</span>
              </span>
            )}
          </div>

          {/* Assignees */}
          <div className="mt-2 text-sm text-gray-600">
            <User className="inline w-3 h-3 mr-1" />
            {getAssigneeNames(task.assignees)}
          </div>

          {/* Progress Bar */}
          {task.percent_complete > 0 && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Progress</span>
                <span>{task.percent_complete}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${task.percent_complete}%` }}
                />
              </div>
            </div>
          )}

          {/* Expanded Content */}
          {expanded && (
            <>
              {task.description && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{renderLinkifiedText(task.description)}</p>
                </div>
              )}
              
              {/* Recurring Template Info */}
              {task.is_recurring_template && (
                <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-purple-700 font-medium text-sm">🔄 Recurring Template</span>
                  </div>
                  <div className="text-xs text-gray-700 space-y-1">
                    {task.recurring_rule && (
                      <div>
                        <span className="font-medium">Rule:</span> <code className="bg-white px-2 py-0.5 rounded">{task.recurring_rule}</code>
                      </div>
                    )}
                    {task.recurring_due_time && (
                      <div>
                        <span className="font-medium">Default Due Time:</span> {formatTimeTo12Hour(task.recurring_due_time)}
                      </div>
                    )}
                    {task.next_generation_date && (
                      <div>
                        <span className="font-medium">Next Task:</span> {formatDate(task.next_generation_date)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => onEdit(task)}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Edit task"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onArchive(task.id, task.archived)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={task.archived ? "Unarchive task" : "Archive task"}
          >
            {task.archived ? (
              <ArchiveRestore className="w-4 h-4 text-blue-600" />
            ) : (
              <Archive className="w-4 h-4 text-gray-600" />
            )}
          </button>
          <button
            onClick={() => onDelete(task.id, task.name)}
            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
            title="Delete task"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Subtasks Toggle */}
      {task.subtask_count > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={() => setShowSubtasks(!showSubtasks)}
            className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {showSubtasks ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span>{task.subtask_count} subtasks</span>
          </button>
        </div>
      )}
    </div>
  );
};

const KanbanTaskCard = ({ task, onEdit, onDelete, onArchive, onStatusChange, stages, teamMembers, onTimer, currentTimer, timerDuration }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleCardClick = (e) => {
    // Only open edit modal if not clicking on expand button or other interactive elements
    if (!e.target.closest('.expand-button') && !e.target.closest('.task-actions') && !isDragging) {
      onEdit(task);
    }
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    if (action === 'edit') {
      onEdit(task);
    } else if (action === 'delete') {
      onDelete(task.id);
    }
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({
      taskId: task.id,
      currentStatus: task.status
    }));
    e.dataTransfer.effectAllowed = 'move';
    
    // Add visual feedback
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    e.target.style.opacity = '1';
  };

  return (
    <div 
      className={`bg-white border rounded-lg hover:shadow-md transition-all cursor-pointer ${
        task.archived ? 'border-gray-300 opacity-75' : 'border-gray-200'
      }`}
      onClick={handleCardClick}
    >
      {/* Collapsed View - Always Visible */}
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1">
            <button
              onClick={handleExpandClick}
              className="expand-button p-1 hover:bg-gray-100 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>
            <h4 className="font-medium text-gray-900 text-sm leading-tight flex-1">
              {task.name}
              {task.is_milestone && <Target className="inline w-3 h-3 ml-1 text-yellow-500" />}
            </h4>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Timer Status Indicator */}
            {currentTimer?.task_id === task.id && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-800 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium">
                  {Math.floor(timerDuration / 60)}h {timerDuration % 60}m
                </span>
              </div>
            )}
            
            {/* Archived Badge */}
            {task.archived && (
              <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                Archived
              </span>
            )}

            {/* Priority Badge - Always Visible */}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${PRIORITIES[task.priority]?.bgColor} ${PRIORITIES[task.priority]?.color}`}>
              {task.priority}
            </span>
          </div>
        </div>
      </div>

      {/* Expanded View - Only When Expanded */}
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          {/* Description */}
          {task.description && (
            <p className="text-gray-600 text-sm mb-3 mt-2 line-clamp-2 whitespace-pre-wrap">
              {renderLinkifiedText(task.description)}
            </p>
          )}

          {/* Task Details */}
          <div className="space-y-2">
            {/* Due Date */}
            {task.due_date && (
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Calendar className="w-3 h-3" />
                <span>
                  Due: {new Date(task.due_date).toLocaleDateString()}
                  {task.due_time && ` at ${formatTimeTo12Hour(task.due_time)}`}
                </span>
              </div>
            )}

            {/* Effort Estimate */}
            {task.estimated_effort && (
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                <span>{task.estimated_effort}</span>
              </div>
            )}

            {/* Category */}
            {task.category && (
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Tag className="w-3 h-3" />
                <span>{task.category}</span>
              </div>
            )}

            {/* Assignees */}
            {task.assignees && task.assignees.length > 0 && (
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <User className="w-3 h-3" />
                <div className="flex items-center space-x-1">
                  {task.assignees.slice(0, 3).map((assigneeId, index) => {
                    const member = teamMembers.find(m => m.user.id === assigneeId);
                    if (!member) return null;
                    
                    const hasAvatar = member.user.avatar_url && 
                                     member.user.avatar_url.trim() !== '' && 
                                     !member.user.avatar_url.includes('example.com');
                    
                    return (
                      <div
                        key={assigneeId}
                        className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium text-white bg-purple-500 rounded-full overflow-hidden"
                        title={member.user.name}
                      >
                        {hasAvatar ? (
                          <img 
                            src={member.user.avatar_url} 
                            alt={member.user.name}
                            className="w-6 h-6 object-cover"
                            onError={(e) => {
                              // Fallback to initial if image fails to load
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = member.user.name.charAt(0).toUpperCase();
                            }}
                          />
                        ) : (
                          member.user.name.charAt(0).toUpperCase()
                        )}
                      </div>
                    );
                  })}
                  {task.assignees.length > 3 && (
                    <span className="text-xs text-gray-400">
                      +{task.assignees.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            {task.tags && task.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {task.tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                {task.tags.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-400 text-xs rounded-full">
                    +{task.tags.length - 3}
                  </span>
                )}
              </div>
            )}

            {/* Milestone Indicator */}
            {task.is_milestone && (
              <div className="flex items-center space-x-2 text-xs text-purple-600">
                <AlertCircle className="w-3 h-3" />
                <span>Milestone</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="task-actions flex justify-end space-x-2 mt-3 pt-2 border-t border-gray-100">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTimer && onTimer(task.id);
              }}
              className={`p-1 rounded transition-colors ${
                currentTimer?.task_id === task.id
                  ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
              }`}
              title={currentTimer?.task_id === task.id ? 'Stop Timer' : 'Start Timer'}
            >
              {currentTimer?.task_id === task.id ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={(e) => handleActionClick(e, 'edit')}
              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
              title="Edit Task"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onArchive && onArchive(task.id, task.archived);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title={task.archived ? "Unarchive task" : "Archive task"}
            >
              {task.archived ? (
                <ArchiveRestore className="w-4 h-4 text-blue-600" />
              ) : (
                <Archive className="w-4 h-4 text-gray-600" />
              )}
            </button>
            <button
              onClick={(e) => handleActionClick(e, 'delete')}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
              title="Delete Task"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// List View Component
const TaskListView = ({ tasks, onEdit, onDelete, onArchive, onStatusChange, stages, teamMembers = [], onTimer, currentTimer, timerDuration }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    
    // Parse date string as LOCAL date (avoid UTC conversion issues)
    // "2025-11-17" should be Nov 17 in local timezone, not UTC
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const dueDateOnly = new Date(year, month - 1, day); // month is 0-indexed
    
    // Get today's date at start of day in local timezone
    const now = new Date();
    const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate difference in days (comparing dates only, no time)
    const diffTime = dueDateOnly - todayOnly;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    // Return relative date if within range
    if (diffDays === 0) {
      return '🔴 Today';
    } else if (diffDays === 1) {
      return '🟢 Tomorrow';
    } else if (diffDays === -1) {
      return 'Yesterday';
    } else if (diffDays < -1) {
      return `${Math.abs(diffDays)}d overdue`;
    } else {
      return dueDateOnly.toLocaleDateString();
    }
  };

  const getAssigneeNames = (assigneeIds) => {
    if (!assigneeIds || assigneeIds.length === 0) return 'Unassigned';
    const names = assigneeIds.map(id => {
      const member = teamMembers.find(m => m.user.id === id);
      return member ? member.user.name : 'Unknown User';
    });
    return names.join(', ');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignee</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tasks.map((task) => (
              <tr key={task.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900 flex items-center">
                        {task.name}
                        {task.is_milestone && <Target className="inline w-3 h-3 ml-1 text-yellow-500" />}
                      </div>
                      {task.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs whitespace-pre-wrap">
                          {renderLinkifiedText(task.description)}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={task.status}
                    onChange={(e) => onStatusChange(task.id, e.target.value)}
                    className="text-xs border border-gray-200 rounded px-2 py-1 !text-gray-900 bg-white"
                  >
                    {stages.map((stage) => (
                      <option key={stage.id || stage} value={stage.name || stage}>{stage.name || stage}</option>
                    ))}
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${PRIORITIES[task.priority]?.bgColor} ${PRIORITIES[task.priority]?.color}`}>
                    {task.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {getAssigneeNames(task.assignees)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(task.due_date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    {currentTimer?.task_id === task.id && (
                      <div className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-800 rounded-full">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-medium">
                          {Math.floor(timerDuration / 60)}h {timerDuration % 60}m
                        </span>
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTimer && onTimer(task.id);
                      }}
                      className={`p-1 rounded transition-colors ${
                        currentTimer?.task_id === task.id
                          ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                          : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title={currentTimer?.task_id === task.id ? 'Stop Timer' : 'Start Timer'}
                    >
                      {currentTimer?.task_id === task.id ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${task.percent_complete || 0}%` }}
                      />
                    </div>
                    <span className="ml-2 text-xs text-gray-500">{task.percent_complete || 0}%</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEdit(task)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit Task"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onArchive(task.id, task.archived)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title={task.archived ? "Unarchive task" : "Archive task"}
                    >
                      {task.archived ? (
                        <ArchiveRestore className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Archive className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                    <button
                      onClick={() => onDelete(task.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Task"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

//  Grid View Component
const TaskGridView = ({ tasks, onEdit, onDelete, onArchive, onStatusChange, stages, teamMembers = [], onTimer, currentTimer, timerDuration }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {tasks.map((task) => (
        <KanbanTaskCard
          key={task.id}
          task={task}
          onEdit={onEdit}
          onDelete={onDelete}
          onArchive={onArchive}
          onStatusChange={onStatusChange}
          stages={stages}
          teamMembers={teamMembers}
          onTimer={onTimer}
          currentTimer={currentTimer}
          timerDuration={timerDuration}
        />
      ))}
    </div>
  );
};


// =============================================
// DRAG AND DROP COMPONENTS
// =============================================

// Drop Indicator Component
const DropIndicator = () => (
  <div className="h-1 bg-purple-500 rounded-full my-1 animate-pulse shadow-lg" />
);

// Simple Draggable Task Card - Only handles dragging, not dropping
const DraggableTaskCard = ({ task, stageName, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'TASK',
    item: { 
      taskId: task.id, 
      sourceStageId: task.stage_id,
      sourceStageName: stageName
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  return (
    <div 
      ref={drag}
      style={{ 
        opacity: isDragging ? 0.4 : 1,
        cursor: 'grab'
      }}
      data-task-id={task.id}
    >
      {children}
    </div>
  );
};

// Droppable Stage Column - Handles ALL drop logic with position calculation
const DroppableStageColumn = ({ stage, stageTasks, children, onDropTask }) => {
  const [dropPreviewIndex, setDropPreviewIndex] = React.useState(null);
  const dropPositionRef = React.useRef(null);
  
  // Get stage info
  const stageId = stage.id || (typeof stage === 'string' && stage.length > 30 ? stage : null);
  const stageName = stage.name || stage;

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'TASK',
    hover: (item, monitor) => {
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        setDropPreviewIndex(null);
        dropPositionRef.current = null;
        return;
      }
      
      // Calculate drop position based on task positions in state (not DOM)
      // This is more reliable than DOM queries
      let newIndex = stageTasks.length; // Default to end
      
      // Get the column element to calculate relative positions
      const dropTargetElement = document.querySelector(`[data-stage-name="${stageName}"]`);
      
      if (dropTargetElement) {
        const taskElements = dropTargetElement.querySelectorAll('[data-task-id]');
        
        for (let i = 0; i < taskElements.length; i++) {
          const taskEl = taskElements[i];
          const taskId = taskEl.getAttribute('data-task-id');
          
          // Skip the dragged item
          if (taskId === item.taskId) continue;
          
          const rect = taskEl.getBoundingClientRect();
          const midY = rect.top + rect.height / 2;
          
          if (clientOffset.y < midY) {
            // Find the actual index in stageTasks for this task
            const taskIndex = stageTasks.findIndex(t => t.id === taskId);
            newIndex = taskIndex >= 0 ? taskIndex : i;
            break;
          }
        }
      }
      
      setDropPreviewIndex(newIndex);
      dropPositionRef.current = newIndex;
    },
    drop: (item, monitor) => {
      // Clear preview immediately
      setDropPreviewIndex(null);
      
      // Use the position we calculated during hover
      const newPosition = dropPositionRef.current !== null ? dropPositionRef.current : stageTasks.length;
      dropPositionRef.current = null;
      
      // Determine the destination stage ID
      let destStageId = stageId;
      
      // If no stage ID, try to get from first task in stage
      if (!destStageId && stageTasks.length > 0) {
        destStageId = stageTasks[0].stage_id;
      }
      
      // If still no stage ID, use stage name as fallback
      if (!destStageId) {
        destStageId = stageName;
      }
      
      console.log('📦 Drop:', {
        taskId: item.taskId,
        from: item.sourceStageName,
        to: stageName,
        position: newPosition,
        destStageId
      });
      
      onDropTask(item, destStageId, newPosition);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  });
  
  // Clear preview when not hovering
  React.useEffect(() => {
    if (!isOver) {
      setDropPreviewIndex(null);
      dropPositionRef.current = null;
    }
  }, [isOver]);

  // Render tasks with drop indicator
  const renderTasksWithDropIndicator = () => {
    const elements = [];
    
    stageTasks.forEach((task, index) => {
      // Show drop indicator before this task if needed
      if (dropPreviewIndex === index) {
        elements.push(<DropIndicator key={`drop-${index}`} />);
      }
      elements.push(
        <div key={task.id}>
          {React.Children.map(children, child => {
            if (React.isValidElement(child) && child.props.task?.id === task.id) {
              return child;
            }
            return null;
          }).filter(Boolean)[0] || null}
        </div>
      );
    });
    
    // Show drop indicator at end if dropping at last position
    if (dropPreviewIndex === stageTasks.length) {
      elements.push(<DropIndicator key="drop-end" />);
    }
    
    return elements;
  };

  return (
    <div 
      ref={drop}
      data-stage-id={stageId}
      data-stage-name={stageName}
      className={`bg-gray-50 rounded-lg p-3 min-h-96 transition-all flex-shrink-0 w-80 ${
        isOver && canDrop ? 'bg-purple-50 ring-2 ring-purple-400 ring-opacity-50' : ''
      }`}
    >
      {children}
    </div>
  );
};

// Updated handleTaskDrop function


const TaskManagement = ({ project, client, teamMembers = [], isFullWidth = false, user }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  const [stages, setStages] = useState(['To Do', 'In Progress', 'In Review', 'Done']);
  const [isCustomStages, setIsCustomStages] = useState(false);
  const [currentView, setCurrentView] = useState('kanban'); // 'kanban', 'list', 'grid'
  const [draggedStage, setDraggedStage] = useState(null);
  const [editingStage, setEditingStage] = useState(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const { setActiveContext } = useChatContext();

  // Timer states
  const [currentTimer, setCurrentTimer] = useState(null);
  const [timerDuration, setTimerDuration] = useState(0);

  // Subtask completion validation states
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // Archive state
  const [showArchived, setShowArchived] = useState(false);
  
  // Show subtasks toggle
  const [showSubtasks, setShowSubtasks] = useState(false);

  // Attachment preview
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!project?.id) {
      setActiveContext({ view: 'tasks', client_id: null, project_id: null });
    }
  }, [project?.id, setActiveContext]);
  
  // Deliverable view modal
  const [viewingDeliverable, setViewingDeliverable] = useState(null);

  // Open attachment preview - useCallback to prevent re-renders
  const openAttachmentPreview = React.useCallback((attachment) => {
    setPreviewAttachment(attachment);
    setShowPreview(true);
  }, []); // Empty deps to ensure this function never changes

  // Close attachment preview
  const closeAttachmentPreview = () => {
    setShowPreview(false);
    setPreviewAttachment(null);
  };

  // Handle task drop for drag-and-drop reordering
  const handleTaskDrop = async (draggedItem, destinationStageId, newPositionIndex) => {
    try {
      // New component uses camelCase
      const sourceStageId = draggedItem.sourceStageId;
      const taskId = draggedItem.taskId;
      
      console.log('🎯 handleTaskDrop:', { taskId, sourceStageId, destinationStageId, newPositionIndex });
      
      // Optimistic UI update
      const taskToMove = tasks.find(t => t.id === taskId);
      if (!taskToMove) {
        console.warn('Task not found:', taskId);
        return;
      }
      
      // Find the destination stage name
      const destStage = stages.find(s => (s.id || s) === destinationStageId);
      const destStageName = destStage?.name || destStage || destinationStageId;
      
      // Remove task from current position and insert at new position
      const otherTasks = tasks.filter(t => t.id !== taskId);
      const tasksInDestStage = otherTasks.filter(t => 
        (t.stage_id === destinationStageId) || (t.status === destStageName)
      );
      const tasksNotInDestStage = otherTasks.filter(t => 
        (t.stage_id !== destinationStageId) && (t.status !== destStageName)
      );
      
      // Update the moved task
      const updatedTask = {
        ...taskToMove,
        stage_id: destinationStageId,
        status: destStageName
      };
      
      // Insert at the correct position
      const newTasksInDestStage = [...tasksInDestStage];
      newTasksInDestStage.splice(newPositionIndex, 0, updatedTask);
      
      // Combine all tasks back
      const updatedTasks = [...tasksNotInDestStage, ...newTasksInDestStage];
      setTasks(updatedTasks);
      
      // Call backend API to persist the change
      await api.put(`/projects/${project.id}/task-order`, {
        task_id: taskId,
        source_stage_id: sourceStageId,
        destination_stage_id: destinationStageId,
        new_position: newPositionIndex
      });
      
      // Don't fetch tasks on success - optimistic update is sufficient
      // This prevents the jarring reset that can occur if server returns different order
      console.log('✅ Task order updated successfully');
      
    } catch (error) {
      console.error('Error updating task order:', error);
      alert('Failed to update task order. Please try again.');
      // Revert optimistic update on error by fetching fresh data
      fetchTasks();
    }
  };


  const handleStageModalSave = async (newStages) => {
    try {
      // Send both new stages and original stages for proper task migration
      const response = await api.put(`/projects/${project.id}/stages`, {
        stages: newStages,
        old_stages: stages  // Original stages for migration mapping
      });
      
      // Show migration results if tasks were moved
      if (response.data.tasks_migrated > 0) {
        // Successfully migrated tasks to new stage names
      }
      
      // Update local state
      setStages(newStages);
      setIsCustomStages(true);
      setShowStageModal(false);
      
      // Refresh tasks to reflect any status changes
      fetchTasks();
    } catch (error) {
      console.error('Failed to save stages:', error);
      throw error; // Let the modal handle the error display
    }
  };

  const loadProjectStages = async () => {
    try {
      // Use stages-full endpoint to get stage objects with IDs (needed for drag-and-drop)
      const response = await api.get(`/projects/${project.id}/stages-full`);
      
      // stages-full returns array of objects with {id, name, ...}
      if (response.data && response.data.length > 0) {
        setStages(response.data);
        setIsCustomStages(true);
      } else {
        // Fallback: try the old endpoint
        const fallbackResponse = await api.get(`/projects/${project.id}/stages`);
        setStages(fallbackResponse.data.stages);
        setIsCustomStages(fallbackResponse.data.is_custom);
      }
    } catch (error) {
      console.error('Failed to load project stages:', error);
      // Use fallback stages if API fails
      setStages(['To Do', 'In Progress', 'In Review', 'Done']);
      setIsCustomStages(false);
    }
  };

  const saveProjectStages = async (newStages) => {
    try {
      const response = await api.put(`/projects/${project.id}/stages`, { stages: newStages });
      setIsCustomStages(response.data.is_custom);
      return true;
    } catch (error) {
      console.error('Failed to save project stages:', error);
      alert('Failed to save stages. Please try again.');
      return false;
    }
  };

  const resetToDefaultStages = async () => {
    try {
      const response = await api.delete(`/projects/${project.id}/stages`);
      setStages(response.data.stages);
      setIsCustomStages(response.data.is_custom);
      return true;
    } catch (error) {
      console.error('Failed to reset stages:', error);
      alert('Failed to reset stages. Please try again.');
      return false;
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await api.get(`/projects/${project.id}/tasks`, {
        params: { include_subtasks: showSubtasks }
      });
      
      // Filter archived tasks unless showArchived is true
      let filteredTasks = response.data || [];
      if (!showArchived) {
        filteredTasks = filteredTasks.filter(task => !task.archived);
      }

      // Normalize task status from stage_name when missing/mismatched
      const stageNames = (stages || []).map((stage) => stage.name || stage);
      const normalizedTasks = filteredTasks.map((task) => {
        const statusValue = task?.status;
        const stageNameValue = task?.stage_name;
        const hasValidStatus = stageNames.includes(statusValue);

        if (!statusValue || !hasValidStatus) {
          if (stageNameValue) {
            return { ...task, status: stageNameValue };
          }
        }

        return task;
      });
      
      setTasks(normalizedTasks);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project?.id) {
      loadProjectStages();
      fetchTasks();
    }
  }, [project, showArchived, showSubtasks]);

  // Listen for task updates from the modal to refresh the Kanban board
  useEffect(() => {
    const handleTaskUpdated = (event) => {
      const { projectId } = event.detail || {};
      // Refresh if the updated task belongs to this project
      if (project?.id && (projectId === project.id || !projectId)) {
        console.log('🔄 Refreshing tasks after task update');
        fetchTasks();
      }
    };
    
    // Handle task deleted events
    const handleTaskDeleted = (event) => {
      const { taskId } = event.detail || {};
      console.log('🗑️ Task deleted event received:', taskId);
      // Always refresh when a task is deleted
      fetchTasks();
    };
    
    window.addEventListener('taskUpdated', handleTaskUpdated);
    window.addEventListener('taskDeleted', handleTaskDeleted);
    return () => {
      window.removeEventListener('taskUpdated', handleTaskUpdated);
      window.removeEventListener('taskDeleted', handleTaskDeleted);
    };
  }, [project?.id]);

  // Fetch current timer status
  useEffect(() => {
    const fetchTimerStatus = async () => {
      try {
        const response = await api.get('/timer/status');
        if (response.data) {
          setCurrentTimer(response.data);
        }
      } catch (error) {
        // No active timer
      }
    };
    
    if (user) {
      fetchTimerStatus();
    }
  }, [user]);

  // Update timer duration every second
  useEffect(() => {
    let interval;
    if (currentTimer?.is_running) {
      interval = setInterval(() => {
        const now = new Date();
        const startTime = new Date(currentTimer.start_time);
        const diffMinutes = Math.floor((now - startTime) / (1000 * 60));
        setTimerDuration(diffMinutes);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentTimer]);

  // Timer functionality
  const handleTaskTimer = async (taskId) => {
    try {
      if (currentTimer?.is_running) {
        // Stop current timer
        await api.post('/timer', {
          action: 'stop',
          task_id: currentTimer.task_id
        });
        setCurrentTimer(null);
        setTimerDuration(0);
      }
      
      // Start new timer if it's a different task or no timer was running
      if (!currentTimer?.is_running || currentTimer?.task_id !== taskId) {
        const response = await api.post('/timer', {
          action: 'start',
          task_id: taskId
        });
        
        setCurrentTimer(response.data);
      }
    } catch (error) {
      console.error('Timer error:', error);
      alert('Error with timer. Please try again.');
    }
  };

  const handleTaskSave = async (taskData) => {
    try {
      let response;
      
      if (editingTask) {
        response = await api.put(`/tasks/${editingTask.id}`, taskData);
      } else {
        // Handle "general" project case - fetch the actual General project
        let projectId = project.id;
        
        if (projectId === 'general' || !projectId) {
          // Fetch General project without client
          const projectsResponse = await api.get('/projects');
          
          const generalProject = projectsResponse.data.find(p => !p.client_id && p.name === 'General');
          
          if (!generalProject) {
            alert('General project not found. Please contact support.');
            return;
          }
          projectId = generalProject.id;
        }
        
        response = await api.post(`/projects/${projectId}/tasks`, taskData);
      }
      
      // Only close modal and refresh for manual saves (not auto-saves)
      if (!taskData._autoSave) {
        setShowTaskModal(false);
        setEditingTask(null);
        fetchTasks();
      } else {
        // For auto-save, update editing task with new ID if it's a new task
        if (!editingTask?.id && response.data?.task?.id) {
          setEditingTask({...taskData, id: response.data.task.id});
        }
      }
      
      // Return the saved task data
      return response.data?.task || response.data;
    } catch (error) {
      console.error('Failed to save task:', error);
      if (!taskData._autoSave) {
        alert('Failed to save task. Please try again.');
      }
      throw error; // Re-throw so auto-save can handle the error
    }
  };

  const handleTaskDelete = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await api.delete(`/tasks/${taskId}`);
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleTaskArchive = async (taskId, currentlyArchived) => {
    const action = currentlyArchived ? 'unarchive' : 'archive';
    if (!confirm(`Are you sure you want to ${action} this task?`)) return;
    
    try {
      await api.put(`/tasks/${taskId}/archive`, { archived: !currentlyArchived });
      fetchTasks();
    } catch (error) {
      console.error(`Failed to ${action} task:`, error);
      alert(`Failed to ${action} task`);
    }
  };


  const handleEditTask = (task) => {
    // Dispatch event to open task modal without navigating away from current view
    // This keeps the project view open while showing the task modal
    window.dispatchEvent(new CustomEvent('openTaskModal', { detail: task }));
  };

  // Create a new task - opens modal in create mode with template support
  const handleCreateNewTask = async (stage = null) => {
    // Dispatch event to open task modal in "create mode" (no existing task)
    // This allows the user to use templates before creating the task
    window.dispatchEvent(new CustomEvent('createNewTask', { 
      detail: { 
        projectId: project?.id,
        stage: stage || stages[0] || 'To Do'
      } 
    }));
  };

  const getTasksByStage = (stage) => {
    return tasks.filter(task => (task.status || task.stage_name) === stage);
  };

  const handleStageDragStart = (e, stage) => {
    setDraggedStage(stage);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleStageDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleStageDrop = async (e, targetStage) => {
    e.preventDefault();
    
    if (!draggedStage || draggedStage === targetStage) {
      setDraggedStage(null);
      return;
    }

    const dragIndex = stages.indexOf(draggedStage);
    const targetIndex = stages.indexOf(targetStage);
    
    // Reorder stages
    const newStages = [...stages];
    newStages.splice(dragIndex, 1);
    newStages.splice(targetIndex, 0, draggedStage);
    
    // Save to backend
    const success = await saveProjectStages(newStages);
    if (success) {
      setStages(newStages);
    }
    
    setDraggedStage(null);
  };

  const handleStageDragEnd = () => {
    setDraggedStage(null);
  };

  const handleTaskStatusChange = async (taskId, newStatus, fromSource = null) => {
    try {
      const token = localStorage.getItem('access_token');
      const task = tasks.find(t => t.id === taskId);
      
      if (!task) {
        console.error('Task not found:', taskId);
        return;
      }

      // Validate task completion if moving to a completion status
      const validation = validateTaskCompletion(task, newStatus);
      
      if (!validation.canComplete) {
        // Store the pending change and show validation modal
        setPendingStatusChange({
          taskId,
          task,
          newStatus,
          previousStatus: task.status,
          fromSource,
          incompleteSubtasks: validation.incompleteSubtasks
        });
        setShowSubtaskModal(true);
        return;
      }

      // If validation passes, proceed with status change
      await performStatusChange(taskId, task, newStatus);
      
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  const performStatusChange = async (taskId, task, newStatus) => {
    try {
      // Find the stage_id for the newStatus (stage name)
      const targetStage = stages.find(s => s.name === newStatus);
      
      await api.put(`/tasks/${taskId}`, { 
        ...task, 
        status: newStatus,
        stage_id: targetStage ? targetStage.id : null  // Send stage_id for project tasks
      });
      
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task status:', error);
      throw error;
    }
  };

  const handleSubtaskModalConfirm = async () => {
    if (!pendingStatusChange) return;

    try {
      // The SubtaskCompletionModal handles marking subtasks complete
      // We just need to proceed with the task status change
      await performStatusChange(
        pendingStatusChange.taskId,
        pendingStatusChange.task,
        pendingStatusChange.newStatus
      );
      
      // Clean up
      setPendingStatusChange(null);
      setShowSubtaskModal(false);
    } catch (error) {
      console.error('Failed to complete task after subtask completion:', error);
    }
  };

  const handleSubtaskModalCancel = () => {
    if (!pendingStatusChange) return;

    // If coming from kanban drag/drop, we need to revert the visual change
    // This would require additional logic in the kanban component to handle
    // For now, we just refresh to ensure consistency
    fetchTasks();
    
    // Clean up
    setPendingStatusChange(null);
    setShowSubtaskModal(false);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-4 gap-4">
            <div className="h-40 bg-gray-100 rounded"></div>
            <div className="h-40 bg-gray-100 rounded"></div>
            <div className="h-40 bg-gray-100 rounded"></div>
            <div className="h-40 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Timer Widget - Show if timer is running */}
      {currentTimer?.is_running && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <Play className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="font-semibold text-green-800">
                  {Math.floor(timerDuration / 60)}h {timerDuration % 60}m
                </div>
                <div className="text-sm text-green-600">
                  {tasks.find(t => t.id === currentTimer.task_id)?.name || 'Active Timer'}
                </div>
              </div>
            </div>
            <button
              onClick={() => handleTaskTimer(currentTimer.task_id)}
              className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Pause className="w-4 h-4" />
              <span>Stop</span>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col space-y-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Task Stages</h2>
            <div className="flex items-center space-x-4 text-sm">
              <p className="text-gray-600">
                {tasks.length} total tasks • {currentView === 'kanban' ? 'Drag and drop to move between stages' : 'Use dropdown to change task status'}
              </p>
              {isCustomStages && (
                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                  Custom Stages
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Sticky Centered Task Controls */}
        <div className="sticky top-0 z-40 bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-center space-x-4">
            {/* View Selector */}
            <div className="flex items-center space-x-1 border border-gray-300 rounded-lg p-1 bg-white">
              <button
                onClick={() => setCurrentView('kanban')}
                className={`flex items-center space-x-1 px-3 py-1 rounded transition-colors text-sm ${
                  currentView === 'kanban' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Columns3 className="w-4 h-4" />
                <span>Kanban</span>
              </button>
              <button
                onClick={() => setCurrentView('list')}
                className={`flex items-center space-x-1 px-3 py-1 rounded transition-colors text-sm ${
                  currentView === 'list' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <List className="w-4 h-4" />
                <span>List</span>
              </button>
              <button
                onClick={() => setCurrentView('grid')}
                className={`flex items-center space-x-1 px-3 py-1 rounded transition-colors text-sm ${
                  currentView === 'grid' 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                <span>Grid</span>
              </button>
            </div>

            {/* Edit Stages Button */}
            <button
              onClick={() => setShowStageModal(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
            >
              <Settings className="w-4 h-4" />
              <span>Edit Stages</span>
            </button>
            
            {/* New Task Button */}
            <button
              onClick={() => handleCreateNewTask()}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span className="!text-white">New Task</span>
            </button>

            {/* Show Subtasks Toggle */}
            <label className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors">
              <input
                type="checkbox"
                checked={showSubtasks}
                onChange={(e) => setShowSubtasks(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-gray-700">Show Subtasks</span>
            </label>

            {/* Show Archived Toggle */}
            <label className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-gray-700">Show Archived</span>
            </label>
          </div>
        </div>
      </div>

      {/* Task Display Views */}
      {currentView === 'kanban' && (
        <div 
          className="overflow-x-auto pb-4 kanban-scrollbar"
          style={{ 
            scrollbarWidth: 'auto',
            scrollbarColor: '#9333ea #e5e7eb'
          }}
        >
          <div className="flex gap-4 pb-2" style={{ minWidth: `${stages.length * 320}px` }}>
          {stages.map((stage, index) => {
            const stageName = stage.name || stage;
            const tasksInStage = getTasksByStage(stageName);
            return (
              <DroppableStageColumn
                key={stageName}
                stage={stage}
                stageTasks={tasksInStage}
                onDropTask={handleTaskDrop}
              >
                {/* Stage Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {/* Drag Handle for Column Reordering */}
                    <div 
                      className="text-gray-400 cursor-move hover:text-gray-600"
                      draggable="true"
                      onDragStart={(e) => {
                        e.stopPropagation();
                        handleStageDragStart(e, stageName);
                      }}
                      onDragEnd={handleStageDragEnd}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                        <circle cx="2" cy="2" r="1"/>
                        <circle cx="6" cy="2" r="1"/>
                        <circle cx="10" cy="2" r="1"/>
                        <circle cx="2" cy="6" r="1"/>
                        <circle cx="6" cy="6" r="1"/>
                        <circle cx="10" cy="6" r="1"/>
                        <circle cx="2" cy="10" r="1"/>
                        <circle cx="6" cy="10" r="1"/>
                        <circle cx="10" cy="10" r="1"/>
                      </svg>
                    </div>

                    <div className="flex items-center space-x-2">
                      <h3 className="text-sm font-semibold !text-gray-900">
                        {stageName}
                      </h3>
                      <span className="bg-gray-200 !text-gray-600 text-xs px-2 py-1 rounded-full">
                        {tasksInStage.length}
                      </span>
                    </div>
                  </div>

                  {/* Stage Actions */}
                  <div className="flex items-center space-x-2">
                    <button 
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => {
                        setEditingStage({ stage: stageName, index });
                        setShowStageModal(true);
                      }}
                    >
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleCreateNewTask(stageName)}
                      className="text-purple-600 hover:text-purple-800 transition-colors"
                      title="Add task to this stage"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Tasks in Stage */}
                <div className="space-y-2 min-h-48" data-stage-container={stageName}>
                  {tasksInStage.map((task) => (
                    <DraggableTaskCard
                      key={task.id}
                      task={task}
                      stageName={stageName}
                    >
                      <KanbanTaskCard
                        task={task}
                        onEdit={handleEditTask}
                        onDelete={handleTaskDelete}
                        onArchive={handleTaskArchive}
                        onStatusChange={handleTaskStatusChange}
                        stages={stages}
                        teamMembers={teamMembers}
                        onTimer={handleTaskTimer}
                        currentTimer={currentTimer}
                        timerDuration={timerDuration}
                      />
                    </DraggableTaskCard>
                  ))}
                  
                  {/* Empty State */}
                  {tasksInStage.length === 0 && (
                    <div className="flex items-center justify-center py-12 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                      <div className="text-center">
                        <div className="text-sm">Drop tasks here</div>
                        <div className="text-xs mt-1">or click + to add new</div>
                      </div>
                    </div>
                  )}
                </div>
              </DroppableStageColumn>
            );
          })}
          </div>
        </div>
      )}

      {currentView === 'list' && (
        <TaskListView
          tasks={tasks}
          onEdit={handleEditTask}
          onDelete={handleTaskDelete}
          onArchive={handleTaskArchive}
          onStatusChange={handleTaskStatusChange}
          stages={stages}
          teamMembers={teamMembers}
          onTimer={handleTaskTimer}
          currentTimer={currentTimer}
          timerDuration={timerDuration}
        />
      )}

      {currentView === 'grid' && (
        <TaskGridView
          tasks={tasks}
          onEdit={handleEditTask}
          onDelete={handleTaskDelete}
          onArchive={handleTaskArchive}
          onStatusChange={handleTaskStatusChange}
          stages={stages}
          teamMembers={teamMembers}
          onTimer={handleTaskTimer}
          currentTimer={currentTimer}
          timerDuration={timerDuration}
        />
      )}

      {/* Stage Edit Modal */}
      <StageEditModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        stages={stages}
        onSave={handleStageModalSave}
        projectId={project?.id}
      />

      {/* Subtask Completion Validation Modal */}
      {pendingStatusChange && (
        <SubtaskCompletionModal
          isOpen={showSubtaskModal}
          onClose={() => setShowSubtaskModal(false)}
          task={pendingStatusChange.task}
          incompleteSubtasks={pendingStatusChange.incompleteSubtasks}
          newStatus={pendingStatusChange.newStatus}
          previousStatus={pendingStatusChange.previousStatus}
          onConfirmComplete={handleSubtaskModalConfirm}
          onCancel={handleSubtaskModalCancel}
        />
      )}

      {/* Attachment Preview Modal */}
      <AttachmentPreview
        attachment={previewAttachment}
        isOpen={showPreview}
        onClose={closeAttachmentPreview}
      />

      {/* Deliverable View Modal */}
      {viewingDeliverable && (
        <DeliverableViewModal
          deliverableId={viewingDeliverable}
          onClose={() => setViewingDeliverable(null)}
          currentUser={user}
          onUpdate={() => {
            // Trigger reload of tasks to refresh deliverables list
            if (editingTask?.id) {
              fetchTasks();
            }
          }}
        />
      )}
    </div>
  );
};

// Wrap TaskManagement with DndProvider for drag-and-drop functionality
const TaskManagementWithDnd = (props) => (
  <DndProvider backend={HTML5Backend}>
    <TaskManagement {...props} />
  </DndProvider>
);

export default TaskManagementWithDnd;
export { TaskModal };
